import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ExternalLink, X, ChevronRight, Building2, Pill, FlaskConical,
  ArrowLeft, ArrowRight, Pencil, Trash2, Plus, FileText, Activity, Star,
  ArrowUpDown, ChevronUp, ChevronDown,
} from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Header from './Header';
import { apiService } from '../services/api';

type ReadoutTrial = {
  trial_id: string;
  trial_name?: string | null;
  nct_id?: string | null;
  sponsors?: string[] | string | null;
  sponsor?: string | null;
  companies?: string[] | string | null;
  tickers?: string[] | string | null;
  drug_aliases?: string[] | string | null;
  assets?: string[] | string | null;
  milestone_type?: string | null;
  indication?: string | null;
  phase?: string | null;
  phases?: string | null;
  overall_status?: string | null;
  brief_summary?: string | null;
  primary_completion_date?: string | null;
  completion_date?: string | null;
  study_start_date?: string | null;
  ctgov_url?: string | null;
  active?: boolean | null;
  timing_phrase?: string | null;
  timing_specificity?: string | null;
  timing_start?: string | null;
  timing_end?: string | null;
  event_type?: string | null;
  observed_at?: string | null;
  observed_at_ts?: string | null;
  document_date?: string | null;
  first_seen_at?: string | null;
  watchlist_document_date?: string | null;
  source_url?: string | null;
  hosted_url?: string | null;
  has_hosted_copy?: boolean | null;
  source_name?: string | null;
  statement_text?: string | null;
  cancer_type?: string[] | string | null;
  latest_guidance?: string | null;
  latest_guidance_source?: 'nct_primary_completion' | 'nct_completion' | 'company_update' | null;
  latest_guidance_source_label?: string | null;
  latest_guidance_note?: string | null;
  ir_crawled_at?: string | null;
  flagged?: boolean | null;
};

type ReadoutCompany = {
  company_id: string | number;
  name: string;
  ticker?: string | null;
  aliases?: string[] | string | null;
  cik?: string | null;
  last_crawled_at?: string | null;
  ir_url?: string | null;
  ir_doc_count?: number | null;
  ir_presentation_count?: number | null;
  unlinked_file_count?: number | null;
};

type ReadoutLastCrawl = {
  last_crawl_at?: string | null;
  sites_crawled?: number | null;
  sites_crawlable?: number | null;
};

function companyDisplayName(c: ReadoutCompany): string {
  const ticker = (c.ticker || '').trim();
  return ticker ? `${c.name} (${ticker})` : c.name;
}

const UNMATCHED_COMPANY = 'Unmatched / no company';
const UNSPECIFIED_ASSET = 'Unspecified asset';

function toList(value?: string[] | string | null): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean).map((v) => String(v).trim()).filter(Boolean);
  const s = String(value).trim();
  return s ? [s] : [];
}

function normalizeKey(value: string): string {
  return value.trim().toLowerCase();
}

function trialCompanies(t: ReadoutTrial): string[] {
  const list = toList(t.companies);
  return list.length > 0 ? list : [UNMATCHED_COMPANY];
}

function trialAssets(t: ReadoutTrial): string[] {
  const list = toList(t.assets?.length ? t.assets : t.drug_aliases);
  return list.length > 0 ? list : [UNSPECIFIED_ASSET];
}

function trialCancerTypes(t: { cancer_type?: string[] | string | null }): string[] {
  return toList(t.cancer_type);
}

/** Display label: Phase 2, Phase 1/2. Arrays / PHASE2 / "Phase 1, Phase 2" → slash form. Empty → em dash. */
function formatPhaseLabel(raw?: string | string[] | null): string {
  if (raw == null) return '—';
  let tokens: string[] = [];
  if (Array.isArray(raw)) {
    tokens = raw.map((v) => String(v).trim()).filter(Boolean);
  } else {
    const s = String(raw).trim();
    if (!s || s === '—' || s === '-' || s === '–' || s === '[]') return '—';
    if (s.startsWith('[')) {
      try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) tokens = parsed.map((v) => String(v).trim()).filter(Boolean);
      } catch {
        tokens = [];
      }
    }
    if (tokens.length === 0) tokens = s.split(/[,;]+/).map((t) => t.trim()).filter(Boolean);
  }
  const tokenMap: Record<string, string> = {
    early_phase1: 'Early Phase 1',
    earlyphase1: 'Early Phase 1',
    phase1: 'Phase 1',
    '1': 'Phase 1',
    phase1b: 'Phase 1b',
    '1b': 'Phase 1b',
    phase2: 'Phase 2',
    '2': 'Phase 2',
    phase3: 'Phase 3',
    '3': 'Phase 3',
    phase4: 'Phase 4',
    '4': 'Phase 4',
    phase1_2: 'Phase 1/2',
    phase12: 'Phase 1/2',
    '1/2': 'Phase 1/2',
    phase2_3: 'Phase 2/3',
    phase23: 'Phase 2/3',
    '2/3': 'Phase 2/3',
  };
  const labels: string[] = [];
  const seen = new Set<string>();
  for (const tok of tokens) {
    const compact = tok.toLowerCase().replace(/phase/g, '').replace(/[^0-9a-z/]/g, '');
    let mapped = tokenMap[compact];
    if (!mapped && /^phase\s/i.test(tok)) {
      mapped = tok.replace(/^phase\s+/i, 'Phase ').trim();
    }
    if (!mapped) mapped = tok.trim();
    if (mapped && !seen.has(mapped)) {
      seen.add(mapped);
      labels.push(mapped);
    }
  }
  if (labels.length === 0) return '—';
  const nums: string[] = [];
  for (const lab of labels) {
    const m = lab.match(/phase\s*([1-4][a-z]?(?:\s*\/\s*[1-4][a-z]?)*)/i);
    if (!m) continue;
    for (const part of m[1].split(/\s*\/\s*/)) {
      const p = part.trim();
      if (p && !nums.includes(p)) nums.push(p);
    }
  }
  if (nums.length > 0 && labels.every((lab) => /^phase\s/i.test(lab))) {
    return `Phase ${nums.join('/')}`;
  }
  return labels.length === 1 ? labels[0] : labels.join(', ');
}

interface HierarchyItem {
  key: string;
  name: string;
  trialCount: number;
  assetCount?: number;
  lastCrawledAt?: string | null;
  irUrl?: string | null;
  unlinkedFileCount?: number;
}

function httpUrl(value?: string | null): string {
  const url = (value || '').trim();
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (/^\/\//.test(url)) return `https:${url}`;
  if (/^[a-z0-9][a-z0-9.-]+\.[a-z]{2,}([/?#]|$)/i.test(url)) return `https://${url}`;
  return '';
}

function tickerFromCompanyLabel(name: string): string {
  const m = String(name || '').trim().match(/\(([A-Za-z]{1,5})\)\s*$/);
  return m ? m[1].toUpperCase() : '';
}

function buildCompanyIrLookup(companies: ReadoutCompany[]): Map<string, string> {
  const map = new Map<string, string>();
  const set = (key: string | null | undefined, url: string) => {
    const k = normalizeKey(String(key || ''));
    if (k && url && !map.has(k)) map.set(k, url);
  };
  companies.forEach((c) => {
    const url = httpUrl(c.ir_url);
    if (!url) return;
    set(companyDisplayName(c), url);
    set(c.name, url);
    set(c.ticker, url);
  });
  return map;
}

function irUrlForCompanyName(name: string, lookup: Map<string, string>): string {
  const raw = String(name || '').trim();
  if (!raw) return '';
  const direct = lookup.get(normalizeKey(raw));
  if (direct) return direct;
  const ticker = tickerFromCompanyLabel(raw);
  if (ticker) {
    const byTicker = lookup.get(normalizeKey(ticker));
    if (byTicker) return byTicker;
  }
  const stripped = raw.replace(/\s*\([A-Za-z]{1,5}\)\s*$/, '').trim();
  return stripped ? (lookup.get(normalizeKey(stripped)) || '') : '';
}

function CompanyIrLink({ href }: { href?: string | null }) {
  const url = httpUrl(href);
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title="Investor relations"
      className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-slate-500 hover:text-indigo-700 hover:underline shrink-0"
    >
      IR
      <ExternalLink className="w-3 h-3" />
    </a>
  );
}

function TrialFlagButton({
  flagged,
  onToggle,
}: {
  flagged: boolean;
  onToggle: (e: React.MouseEvent) => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      title={flagged ? 'Remove from My trials' : 'Add to My trials'}
      aria-pressed={flagged}
      aria-label={flagged ? 'Remove from My trials' : 'Add to My trials'}
      className={`p-1 rounded hover:bg-amber-50 ${
        flagged ? 'text-amber-500' : 'text-slate-300 hover:text-amber-400'
      }`}
    >
      <Star className="w-3.5 h-3.5" fill={flagged ? 'currentColor' : 'none'} />
    </button>
  );
}

type DrillLevel = 'updates' | 'companies' | 'assets' | 'trials' | 'cancer_type' | 'flagged';
type TrackerSortKey = 'readout' | 'event';
type TrackerSortDir = 'asc' | 'desc';

const ESTIMATED_READOUT_LABEL = 'Estimated Data Readout';
const EVENT_DATE_LABEL = 'Event date';

type ReadoutAlert = {
  trial_id: string;
  trial_name?: string | null;
  nct_id?: string | null;
  sponsor?: string | null;
  indication?: string | null;
  phase?: string | null;
  assets?: string[] | string | null;
  drug_aliases?: string[] | string | null;
  event_type?: string | null;
  message?: string | null;
  source_url?: string | null;
  hosted_url?: string | null;
  has_hosted_copy?: boolean | null;
  ctgov_url?: string | null;
  created_at?: string | null;
  document_date?: string | null;
  status?: string | null;
  cancer_type?: string[] | string | null;
  companies?: string[] | string | null;
};

type TrialSource = {
  kind: string;
  title?: string | null;
  url: string;
  hosted_url?: string | null;
  source_url?: string | null;
  has_hosted_copy?: boolean | null;
  source_name?: string | null;
  document_date?: string | null;
  observed_at?: string | null;
  trial_id?: string | null;
  trial_name?: string | null;
  nct_id?: string | null;
  assets?: string[] | string | null;
  companies?: string[] | string | null;
  cancer_type?: string[] | string | null;
};

type ReadoutUpdate = {
  update_id: string;
  trial_id: string;
  trial_name?: string | null;
  nct_id?: string | null;
  companies?: string[] | string | null;
  assets?: string[] | string | null;
  cancer_type?: string[] | string | null;
  indication?: string | null;
  phase?: string | string[] | null;
  phases?: string | string[] | null;
  url?: string | null;
  source_url?: string | null;
  hosted_url?: string | null;
  has_hosted_copy?: boolean | null;
  source_name?: string | null;
  source_kind?: string | null;
  observed_at?: string | null;
  document_date?: string | null;
  event_type?: string | null;
  timing_phrase?: string | null;
  statement_text?: string | null;
  update_type?: 'new_data' | 'new_guidance' | 'other_update' | string | null;
  summary?: string | null;
  headline?: string | null;
  flagged?: boolean | null;
  ir_url?: string | null;
  ir_urls?: Array<string | null> | null;
  links?: TrialSource[];
  latest_guidance?: string | null;
  latest_guidance_source?: 'nct_primary_completion' | 'nct_completion' | 'company_update' | null;
  latest_guidance_source_label?: string | null;
  latest_guidance_note?: string | null;
  timing_specificity?: string | null;
  primary_completion_date?: string | null;
  completion_date?: string | null;
  ctgov_url?: string | null;
};

type ReadoutRun = {
  started_at?: string | null;
  finished_at?: string | null;
  docs_fetched?: number | null;
  docs_matched?: number | null;
  extractions?: number | null;
  errors?: string | null;
  notes?: string | null;
};

type UnmatchedCompany = {
  id: number;
  kind?: string | null;
  trial_id?: string | null;
  source_id?: string | null;
  raw_name: string;
  suggested_company_id?: string | null;
};

type TimelineEvent = {
  id: number;
  observed_at?: string | null;
  observed_at_ts?: string | null;
  document_date?: string | null;
  created_at?: string | null;
  timing_phrase?: string | null;
  timing_specificity?: string | null;
  timing_start?: string | null;
  timing_end?: string | null;
  event_type?: string | null;
  source_url?: string | null;
  hosted_url?: string | null;
  has_hosted_copy?: boolean | null;
  source_name?: string | null;
  statement_text?: string | null;
};

const TIMING_SPECIFICITY_OPTIONS = ['none', 'year', 'half-year', 'quarter', 'month', 'imminent', 'actual-result'];
const EVENT_TYPE_OPTIONS = ['readout', 'narrowing', 'shift', 'new', 'widening', 'company_unmatched'];

interface TimelineEntryFormValue {
  timing_phrase: string;
  timing_specificity: string;
  timing_start: string;
  timing_end: string;
  event_type: string;
  observed_at: string;
  source_url: string;
  source_name: string;
  statement_text: string;
}

const BLANK_TIMELINE_FORM: TimelineEntryFormValue = {
  timing_phrase: '', timing_specificity: '', timing_start: '', timing_end: '',
  event_type: '', observed_at: '', source_url: '', source_name: '', statement_text: '',
};

function eventToFormValue(e: TimelineEvent): TimelineEntryFormValue {
  return {
    timing_phrase: e.timing_phrase || '',
    timing_specificity: e.timing_specificity || '',
    timing_start: e.timing_start || '',
    timing_end: e.timing_end || '',
    event_type: e.event_type || '',
    observed_at: e.observed_at || '',
    source_url: e.source_url || '',
    source_name: e.source_name || '',
    statement_text: e.statement_text || '',
  };
}

/** Shared form for both editing an existing readout statement and adding a manual one —
 * superuser-only, used inline in the trial timeline modal. */
function TimelineEntryForm({
  value, onChange, onSave, onCancel, saving,
}: {
  value: TimelineEntryFormValue;
  onChange: (v: TimelineEntryFormValue) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const set = (k: keyof TimelineEntryFormValue) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    onChange({ ...value, [k]: e.target.value });
  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <input value={value.timing_phrase} onChange={set('timing_phrase')} placeholder="Timing phrase (e.g. 'H2 2026')"
          className="rounded border border-slate-200 px-2 py-1.5 text-xs col-span-2" />
        <select value={value.timing_specificity} onChange={set('timing_specificity')} className="rounded border border-slate-200 px-2 py-1.5 text-xs">
          <option value="">Specificity…</option>
          {TIMING_SPECIFICITY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <select value={value.event_type} onChange={set('event_type')} className="rounded border border-slate-200 px-2 py-1.5 text-xs">
          <option value="">Event type…</option>
          {EVENT_TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <input value={value.timing_start} onChange={set('timing_start')} placeholder="Window start (e.g. 2026-07-01)"
          className="rounded border border-slate-200 px-2 py-1.5 text-xs" />
        <input value={value.timing_end} onChange={set('timing_end')} placeholder="Window end"
          className="rounded border border-slate-200 px-2 py-1.5 text-xs" />
        <input value={value.observed_at} onChange={set('observed_at')} placeholder="Observed at (blank = now)"
          className="rounded border border-slate-200 px-2 py-1.5 text-xs" />
        <input value={value.source_name} onChange={set('source_name')} placeholder="Source name"
          className="rounded border border-slate-200 px-2 py-1.5 text-xs" />
        <input value={value.source_url} onChange={set('source_url')} placeholder="Source URL"
          className="rounded border border-slate-200 px-2 py-1.5 text-xs col-span-2" />
        <textarea value={value.statement_text} onChange={set('statement_text')} placeholder="Statement text"
          rows={2} className="rounded border border-slate-200 px-2 py-1.5 text-xs col-span-2" />
      </div>
      <div className="flex gap-2">
        <button type="button" disabled={saving} onClick={onSave}
          className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold disabled:opacity-50">
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" disabled={saving} onClick={onCancel}
          className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold">
          Cancel
        </button>
      </div>
    </div>
  );
}

interface WatchlistFormValue {
  trial_name: string;
  nct_id: string;
  indication: string;
  milestone_type: string;
  sponsors: string;
  drug_aliases: string;
  active: boolean;
}

function sponsorsText(sponsors?: string[] | string | null): string {
  if (!sponsors) return '—';
  if (Array.isArray(sponsors)) return sponsors.filter(Boolean).join(', ') || '—';
  return String(sponsors);
}

function shortTs(value?: string | null): string {
  if (!value) return '';
  return String(value).slice(0, 16).replace('T', ' ');
}

/** America/New_York with EST/EDT abbreviation, e.g. 2026-08-23 3:24 PM EDT */
function formatPacific(value?: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).formatToParts(d);
  const grab = (type: string) => parts.find((p) => p.type === type)?.value || '';
  const year = grab('year');
  const month = grab('month');
  const day = grab('day');
  const hour = grab('hour');
  const minute = grab('minute');
  const dayPeriod = grab('dayPeriod');
  const zone = grab('timeZoneName') || 'PT';
  if (!year || !month || !day) return '';
  return `${year}-${month}-${day} ${hour}:${minute} ${dayPeriod} ${zone}`;
}

function SpecPill({ value }: { value?: string | null }) {
  if (!value) return null;
  const key = String(value).toLowerCase().replace(/[^a-z0-9-]+/g, '-');
  const colors: Record<string, string> = {
    none: 'bg-slate-100 text-slate-600',
    year: 'bg-indigo-50 text-indigo-800',
    'half-year': 'bg-indigo-100 text-indigo-900',
    quarter: 'bg-blue-100 text-blue-800',
    month: 'bg-emerald-100 text-emerald-800',
    imminent: 'bg-amber-100 text-amber-800',
    'actual-result': 'bg-emerald-100 text-emerald-900',
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${colors[key] || 'bg-slate-100 text-slate-600'}`}>
      {value}
    </span>
  );
}

const UPDATE_TYPE_OPTIONS = [
  { id: 'new_data', label: 'New Data' },
  { id: 'new_guidance', label: 'New Guidance' },
  { id: 'other_update', label: 'Other Update' },
] as const;

type UpdateTypeId = (typeof UPDATE_TYPE_OPTIONS)[number]['id'];

function updateTypeLabel(value?: string | null): string {
  const hit = UPDATE_TYPE_OPTIONS.find((o) => o.id === value);
  return hit ? hit.label : 'Other Update';
}

function UpdateTypeChip({ value }: { value?: string | null }) {
  const key = (value || 'other_update') as UpdateTypeId | string;
  const colors: Record<string, string> = {
    new_data: 'bg-emerald-100 text-emerald-900',
    new_guidance: 'bg-indigo-100 text-indigo-900',
    other_update: 'bg-slate-100 text-slate-700',
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${colors[key] || colors.other_update}`}>
      {updateTypeLabel(key)}
    </span>
  );
}

function isoGuidanceDate(value?: string | null): string | null {
  if (!value) return null;
  const text = String(value).trim();
  return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : text || null;
}

const MONTH_TO_Q: Record<string, number> = {
  jan: 1, january: 1, feb: 1, february: 1, mar: 1, march: 1,
  apr: 2, april: 2, may: 2, jun: 2, june: 2,
  jul: 3, july: 3, aug: 3, august: 3, sep: 3, sept: 3, september: 3,
  oct: 4, october: 4, nov: 4, november: 4, dec: 4, december: 4,
};

const QUARTER_NAME: Record<string, number> = {
  first: 1, '1st': 1, second: 2, '2nd': 2, third: 3, '3rd': 3, fourth: 4, '4th': 4,
};

/** Company/NCT windows as 1Q2027 / 1H2027 / YE2027 / 2026 / 2030+. */
function canonicalizeGuidance(value?: string | null): string | null {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  if (/^\d[QH]\d{4}$/i.test(raw) || /^YE\d{4}$/i.test(raw) || /^\d{4}\+?$/.test(raw)) {
    return raw.toUpperCase().replace(/^(\d)H/i, '$1H').replace(/^(\d)Q/i, '$1Q');
  }
  const text = raw.replace(/[–—]/g, '-').replace(/\s+/g, ' ');
  const lower = text.toLowerCase();
  let m = lower.match(/\b(20\d{2})\s*\+/);
  if (m) return `${m[1]}+`;
  m = lower.match(/\b(?:q\s*([1-4])|([1-4])\s*q)\s*['’]?(20\d{2})\b/i);
  if (m) return `${Number(m[1] || m[2])}Q${m[3]}`;
  m = lower.match(
    /\b(first|second|third|fourth|1st|2nd|3rd|4th)\s+quarter(?:\s+of(?:\s+the)?(?:\s+calendar)?(?:\s+year)?)?\s*(?:cy\s*)?(20\d{2})\b/i,
  );
  if (m) return `${QUARTER_NAME[m[1]]}Q${m[2]}`;
  m = lower.match(/\b(?:h\s*([12])|([12])\s*h)\s*['’]?(20\d{2})\b/i);
  if (m) return `${Number(m[1] || m[2])}H${m[3]}`;
  m = lower.match(
    /\b(first|second)\s+half(?:\s+of(?:\s+the)?(?:\s+calendar)?(?:\s+year)?)?\s*(?:cy\s*)?(20\d{2})\b/i,
  );
  if (m) return `${m[1] === 'second' ? 2 : 1}H${m[2]}`;
  m = lower.match(/\b(?:year[- ]end|ye)\s*(?:of\s+)?(20\d{2})\b/i);
  if (m) return `YE${m[1]}`;
  m = lower.match(/\blate\s*(20\d{2})\b/i);
  if (m) return `2H${m[1]}`;
  m = lower.match(
    /\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)[a-z]*\s*(?:-|\/|to|through)\s*(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)[a-z]*\s+(20\d{2})\b/i,
  );
  if (m) {
    const q1 = MONTH_TO_Q[m[1].slice(0, 3).toLowerCase()] || MONTH_TO_Q[m[1].toLowerCase()];
    const q2 = MONTH_TO_Q[m[2].slice(0, 3).toLowerCase()] || MONTH_TO_Q[m[2].toLowerCase()];
    if (q1 && q2 && q1 === q2) return `${q1}Q${m[3]}`;
    if (q1 === 1 && q2 === 2) return `1H${m[3]}`;
    if (q1 === 3 && q2 === 4) return `2H${m[3]}`;
    if (q1 && q2) return m[3];
  }
  m = lower.match(
    /\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)[a-z]*\s+(20\d{2})\b/i,
  );
  if (m) {
    const key = m[1].toLowerCase();
    const q = MONTH_TO_Q[key] || MONTH_TO_Q[key.slice(0, 3)];
    if (q) return `${q}Q${m[2]}`;
  }
  m = raw.match(/\b(20\d{2})-(\d{2})(?:-\d{2})?\b/);
  if (m) {
    const month = Number(m[2]);
    if (month >= 1 && month <= 12) return `${Math.floor((month - 1) / 3) + 1}Q${m[1]}`;
  }
  m = lower.match(/\b(20\d{2})\b/);
  return m ? m[1] : null;
}

function formatGuidanceLabel(value?: string | null): string {
  return canonicalizeGuidance(value) || (value ? String(value).trim() : '');
}

function formatDocDate(value?: string | null): string {
  if (!value) return '';
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  return '';
}

type GuidanceFields = Pick<
  ReadoutTrial,
  | 'latest_guidance'
  | 'latest_guidance_source'
  | 'latest_guidance_source_label'
  | 'latest_guidance_note'
  | 'timing_phrase'
  | 'timing_start'
  | 'timing_specificity'
  | 'hosted_url'
  | 'source_url'
  | 'ctgov_url'
  | 'primary_completion_date'
  | 'completion_date'
>;

function resolveGuidance(trial?: GuidanceFields | null): GuidanceFields {
  const base = trial || {};
  if (base.latest_guidance) return base;
  const pcd = isoGuidanceDate(base.primary_completion_date);
  if (pcd) {
    return {
      ...base,
      latest_guidance: pcd,
      latest_guidance_source: base.latest_guidance_source || 'nct_primary_completion',
      latest_guidance_source_label:
        base.latest_guidance_source_label || 'NCT primary completion · ClinicalTrials.gov',
    };
  }
  const cd = isoGuidanceDate(base.completion_date);
  if (cd) {
    return {
      ...base,
      latest_guidance: cd,
      latest_guidance_source: base.latest_guidance_source || 'nct_completion',
      latest_guidance_source_label:
        base.latest_guidance_source_label || 'NCT study completion · ClinicalTrials.gov',
    };
  }
  return base;
}

function oneYearAgoIso(from = new Date()): string {
  const past = new Date(from.getFullYear() - 1, from.getMonth(), from.getDate());
  const y = past.getFullYear();
  const m = String(past.getMonth() + 1).padStart(2, '0');
  const d = String(past.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Best YYYY-MM-DD for sorting/hiding Estimated Data Readout (ISO, then window start, then Q/H/year). */
function readoutSortDate(trial?: GuidanceFields | null): string | null {
  const resolved = resolveGuidance(trial);
  return parseLooseDate(resolved.latest_guidance)
    || parseLooseDate(resolved.timing_start)
    || parseLooseDate(resolved.timing_phrase);
}

function eventSortDate(
  row: { document_date?: string | null },
  recency?: string | null,
): string | null {
  return parseLooseDate(row.document_date) || parseLooseDate(recency);
}

function parseLooseDate(value?: string | null): string | null {
  if (!value) return null;
  const text = String(value).trim();
  if (!text) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const canon = canonicalizeGuidance(text);
  if (canon) {
    let m = canon.match(/^([1-4])Q(20\d{2})$/i);
    if (m) return `${m[2]}-${String((Number(m[1]) - 1) * 3 + 1).padStart(2, '0')}-01`;
    m = canon.match(/^([12])H(20\d{2})$/i);
    if (m) return `${m[2]}-${m[1] === '1' ? '01' : '07'}-01`;
    m = canon.match(/^YE(20\d{2})$/i);
    if (m) return `${m[1]}-10-01`;
    m = canon.match(/^(20\d{2})\+?$/);
    if (m) return `${m[1]}-01-01`;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  let m = text.match(/\b(?:Q([1-4])|([1-4])Q)\s*['’]?(20\d{2})\b/i);
  if (m) {
    const q = Number(m[1] || m[2]);
    return `${m[3]}-${String((q - 1) * 3 + 1).padStart(2, '0')}-01`;
  }
  m = text.match(/\b(?:H([12])|([12])H)\s*['’]?(20\d{2})\b/i);
  if (m) {
    const h = Number(m[1] || m[2]);
    return `${m[3]}-${h === 1 ? '01' : '07'}-01`;
  }
  m = text.match(/\b(20\d{2})\b/);
  return m ? `${m[1]}-01-01` : null;
}

function defaultSortDir(key: TrackerSortKey): TrackerSortDir {
  return key === 'event' ? 'desc' : 'asc';
}

function compareSortDates(a: string | null, b: string | null, dir: TrackerSortDir): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  const cmp = a.localeCompare(b);
  return dir === 'asc' ? cmp : -cmp;
}

function isPastDate(iso: string | null, cutoff: string): boolean {
  return Boolean(iso && iso < cutoff);
}

function applySortHide<T>(
  rows: T[],
  readoutOf: (row: T) => string | null,
  eventOf: (row: T) => string | null,
  sortKey: TrackerSortKey,
  dir: TrackerSortDir,
  showPast: boolean,
  cutoff: string,
): { visible: T[]; hiddenPast: number } {
  const dateOf = (row: T) => (sortKey === 'event' ? eventOf(row) : readoutOf(row));
  let hiddenPast = 0;
  const kept = showPast
    ? rows
    : rows.filter((row) => {
        if (isPastDate(dateOf(row), cutoff)) {
          hiddenPast += 1;
          return false;
        }
        return true;
      });
  const visible = [...kept].sort((a, b) => compareSortDates(dateOf(a), dateOf(b), dir));
  return { visible, hiddenPast };
}

function SortableTh({
  label, column, active, dir, onSort, className,
}: {
  label: string;
  column: TrackerSortKey;
  active: boolean;
  dir: TrackerSortDir;
  onSort: (key: TrackerSortKey) => void;
  className?: string;
}) {
  return (
    <th className={`px-3 py-2 text-left font-semibold ${className || ''}`}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className={`inline-flex items-center gap-1 hover:text-indigo-700 ${active ? 'text-indigo-800' : ''}`}
        aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <span>{label}</span>
        {active ? (
          dir === 'asc' ? <ChevronUp className="w-3.5 h-3.5 shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 shrink-0" />
        ) : (
          <ArrowUpDown className="w-3 h-3 shrink-0 opacity-40" />
        )}
      </button>
    </th>
  );
}

function GuidanceBlock({
  trial,
  compact = false,
}: {
  trial?: GuidanceFields | null;
  compact?: boolean;
}) {
  const resolved = resolveGuidance(trial);
  const date = formatGuidanceLabel(resolved.latest_guidance || resolved.timing_phrase);
  const source = resolved.latest_guidance_source;
  const label = resolved.latest_guidance_source_label;
  const note = resolved.latest_guidance_note;
  const href = source === 'company_update'
    ? (resolved.hosted_url || resolved.source_url || undefined)
    : (resolved.ctgov_url || resolved.source_url || undefined);
  const chipClass = source === 'company_update'
    ? 'bg-emerald-50 text-emerald-800'
    : source
      ? 'bg-slate-100 text-slate-700'
      : 'bg-amber-50 text-amber-800';
  return (
    <div className={compact ? 'space-y-1' : 'space-y-1.5'}>
      <div className={date ? 'font-medium text-slate-800' : 'font-medium text-slate-500'}>
        {date || 'Unknown'}
      </div>
      {label ? (
        href ? (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold hover:underline ${chipClass}`}
          >
            {label}
          </a>
        ) : (
          <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${chipClass}`}>
            {label}
          </span>
        )
      ) : null}
      {note && source !== 'company_update' ? (
        <div className="text-[10px] text-amber-700">{note}</div>
      ) : null}
      {resolved.timing_specificity && source === 'company_update' ? (
        <div className="mt-0.5"><SpecPill value={resolved.timing_specificity} /></div>
      ) : null}
    </div>
  );
}

/** Grid of clickable cards for the company/asset drill-down levels — plain client-side list,
 * no per-card data fetch, so rendering hundreds of these is still instant. */
function HierarchyGrid({
  items, icon, subtitle, emptyLabel, onOpen,
}: {
  items: HierarchyItem[];
  icon: React.ReactNode;
  subtitle: (item: HierarchyItem) => string | null;
  emptyLabel: string;
  onOpen: (key: string) => void;
}) {
  if (items.length === 0) {
    return <div className="px-4 py-10 text-center text-sm text-slate-500">{emptyLabel}</div>;
  }
  return (
    <div className="p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          onClick={() => onOpen(item.key)}
          className="text-left bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-300 rounded-xl px-3.5 py-3 transition-colors"
        >
          <div className="flex items-center gap-2">
            {icon}
            <span className="font-semibold text-sm text-slate-900 truncate">{item.name}</span>
          </div>
          <div className="text-[11px] text-slate-500 mt-1.5">
            {item.trialCount} trial{item.trialCount === 1 ? '' : 's'}
            {subtitle(item) ? ` · ${subtitle(item)}` : ''}
          </div>
          {item.lastCrawledAt ? (
            <div className="text-[10px] text-slate-400 mt-1">
              Last crawl {formatPacific(item.lastCrawledAt)}
            </div>
          ) : null}
          {item.irUrl ? (
            <a
              href={item.irUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:underline mt-1.5"
            >
              Investor relations
              <ExternalLink className="w-3 h-3" />
            </a>
          ) : null}
        </button>
      ))}
    </div>
  );
}

function fileOpenUrl(item: { hosted_url?: string | null; url?: string | null; source_url?: string | null }): string {
  return (item.hosted_url || item.url || '').trim();
}

function fileCompanyUrl(item: { hosted_url?: string | null; url?: string | null; source_url?: string | null }): string {
  const company = (item.source_url || '').trim();
  const open = fileOpenUrl(item);
  if (company && company !== open && /^https?:\/\//i.test(company)) return company;
  return '';
}

function SourceLinks({
  url, hostedUrl, sourceUrl, kind, title, trialName, nctId, assets, onStop,
}: {
  url?: string | null;
  hostedUrl?: string | null;
  sourceUrl?: string | null;
  kind?: string | null;
  title?: string | null;
  trialName?: string | null;
  nctId?: string | null;
  assets?: string[];
  onStop?: (e: React.MouseEvent) => void;
}) {
  const openUrl = fileOpenUrl({ hosted_url: hostedUrl, url, source_url: sourceUrl });
  const companyUrl = fileCompanyUrl({ hosted_url: hostedUrl, url, source_url: sourceUrl });
  const isPdf = kind === 'presentation_pdf';
  const label = isPdf ? 'Open PDF' : 'Open PR';
  if (!openUrl && !companyUrl) {
    return <span className="text-slate-400">Unavailable</span>;
  }
  const href = openUrl || companyUrl;
  return (
    <span className="break-all">
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        onClick={onStop}
        className="text-indigo-600 hover:underline"
      >
        <span className="font-semibold">{label}</span>
        {' · '}
        {title || href}
      </a>
      {trialName ? <span className="text-slate-500"> · {trialName}{nctId ? ` (${nctId})` : ''}</span> : null}
      {assets && assets.length > 0 ? <span className="text-slate-500"> · {assets.slice(0, 2).join(', ')}</span> : null}
      {companyUrl ? (
        <>
          {' '}
          <a
            href={companyUrl}
            target="_blank"
            rel="noreferrer"
            onClick={onStop}
            className="text-slate-400 hover:underline"
            title="Original company URL"
          >
            source
          </a>
        </>
      ) : null}
    </span>
  );
}

function UpdatesTable({
  rows, flaggedIds, trialById, irLookup, onToggleFlag, onOpenTrial, onOpenCompany, onOpenAsset, onOpenCancer,
  sortKey, sortDir, onSort, emptyLabel,
}: {
  rows: ReadoutUpdate[];
  flaggedIds: Set<string>;
  trialById?: Map<string, ReadoutTrial>;
  irLookup: Map<string, string>;
  onToggleFlag: (trialId: string, e: React.MouseEvent) => void;
  onOpenTrial: (trialId: string) => void;
  onOpenCompany: (key: string) => void;
  onOpenAsset: (key: string) => void;
  onOpenCancer: (name: string) => void;
  sortKey: TrackerSortKey;
  sortDir: TrackerSortDir;
  onSort: (key: TrackerSortKey) => void;
  emptyLabel?: string;
}) {
  if (rows.length === 0) {
    return (
      <div className="px-4 py-10 text-center text-sm text-slate-500">
        {emptyLabel || 'No updates match.'}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-[1280px] w-full text-xs">
        <thead className="bg-slate-100 text-slate-700">
          <tr>
            <th className="px-2 py-2 w-8" aria-label="Flag" />
            <th className="px-3 py-2 text-left font-semibold">Company</th>
            <th className="px-3 py-2 text-left font-semibold">Asset</th>
            <th className="px-3 py-2 text-left font-semibold">Phase</th>
            <SortableTh
              label={ESTIMATED_READOUT_LABEL}
              column="readout"
              active={sortKey === 'readout'}
              dir={sortDir}
              onSort={onSort}
              className="min-w-[160px]"
            />
            <th className="px-3 py-2 text-left font-semibold">Trial</th>
            <th className="px-3 py-2 text-left font-semibold">Cancer type</th>
            <th className="px-3 py-2 text-left font-semibold">Type</th>
            <th className="px-3 py-2 text-left font-semibold">PR / PDF</th>
            <SortableTh
              label={EVENT_DATE_LABEL}
              column="event"
              active={sortKey === 'event'}
              dir={sortDir}
              onSort={onSort}
            />
          </tr>
        </thead>
        <tbody>
          {rows.map((u) => {
            const phaseLabel = formatPhaseLabel(u.phase ?? u.phases);
            return (
            <tr key={`${u.update_id}-${u.trial_id}`} className="border-t border-slate-100 align-top hover:bg-indigo-50/40">
              <td className="px-2 py-2 align-top">
                <TrialFlagButton
                  flagged={Boolean(u.flagged) || flaggedIds.has(u.trial_id)}
                  onToggle={(e) => onToggleFlag(u.trial_id, e)}
                />
              </td>
              <td className="px-3 py-2 min-w-[140px]">
                {toList(u.companies).length === 0 ? (
                  <span className="text-slate-400">{UNMATCHED_COMPANY}</span>
                ) : (
                  toList(u.companies).map((c, idx) => (
                    <div key={c} className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => onOpenCompany(normalizeKey(c))}
                        className="text-left text-indigo-700 hover:underline font-semibold"
                      >
                        {c}
                      </button>
                      <CompanyIrLink href={u.ir_urls?.[idx] || u.ir_url || irUrlForCompanyName(c, irLookup)} />
                    </div>
                  ))
                )}
              </td>
              <td className="px-3 py-2 min-w-[120px]">
                {(toList(u.assets).length ? toList(u.assets) : [UNSPECIFIED_ASSET]).map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => onOpenAsset(normalizeKey(a))}
                    className="block text-left text-indigo-700 hover:underline"
                  >
                    {a}
                  </button>
                ))}
              </td>
              <td className={`px-3 py-2 whitespace-nowrap ${phaseLabel === '—' ? 'text-slate-400' : 'text-slate-700'}`}>
                {phaseLabel}
              </td>
              <td className="px-3 py-2 min-w-[160px]">
                <GuidanceBlock trial={trialById?.get(u.trial_id) || u} compact />
              </td>
              <td className="px-3 py-2 min-w-[180px] max-w-[280px]">
                <button
                  type="button"
                  onClick={() => onOpenTrial(u.trial_id)}
                  title={u.trial_name || u.trial_id || ''}
                  className="font-semibold text-indigo-700 hover:underline text-left line-clamp-3"
                >
                  {u.trial_name && u.trial_name !== u.trial_id && u.trial_name !== u.nct_id
                    ? u.trial_name
                    : u.trial_id}
                </button>
                {u.nct_id ? (
                  <a
                    href={`https://clinicaltrials.gov/study/${u.nct_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="block text-[11px] text-slate-500 hover:text-indigo-600 mt-0.5"
                  >
                    {u.nct_id}
                  </a>
                ) : null}
              </td>
              <td className="px-3 py-2">
                {trialCancerTypes(u).length === 0 ? (
                  <span className="text-slate-400">—</span>
                ) : (
                  trialCancerTypes(u).map((ct) => (
                    <button
                      key={ct}
                      type="button"
                      onClick={() => onOpenCancer(ct)}
                      className="block text-left text-indigo-700 hover:underline"
                    >
                      {ct}
                    </button>
                  ))
                )}
              </td>
              <td className="px-3 py-2 whitespace-nowrap">
                <UpdateTypeChip value={u.update_type} />
              </td>
              <td className="px-3 py-2 min-w-[220px]">
                <SourceLinks
                  url={u.url || u.hosted_url || u.source_url}
                  hostedUrl={u.hosted_url}
                  sourceUrl={u.source_url}
                  kind={u.source_kind}
                  title={u.summary || u.headline || u.source_name}
                  trialName={undefined}
                  nctId={undefined}
                  assets={[]}
                />
              </td>
              <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                {formatDocDate(u.document_date) || '—'}
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function FilesStrip({
  files, onOpenTrial, heading,
}: {
  files: { url: string; hosted_url?: string | null; source_url?: string | null; kind: string; title: string; trial_id?: string; trial_name?: string | null; nct_id?: string | null; assets?: string[]; companies?: string[] }[];
  onOpenTrial?: (trialId: string) => void;
  heading?: string;
}) {
  if (files.length === 0) return null;
  return (
    <div className="px-4 py-3 border-t border-slate-100 space-y-1.5">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{heading || 'Press releases & PDFs'}</div>
      {files.slice(0, 40).map((f, idx) => (
        <div key={`${f.url}-${f.trial_id || idx}`} className="text-xs flex flex-wrap items-baseline gap-x-2">
          <SourceLinks
            url={f.url}
            hostedUrl={f.hosted_url}
            sourceUrl={f.source_url}
            kind={f.kind}
            title={f.title}
            trialName={f.trial_name}
            nctId={f.nct_id}
            assets={f.assets || []}
          />
          {f.trial_id && onOpenTrial ? (
            <button type="button" onClick={() => onOpenTrial(f.trial_id as string)} className="text-slate-500 hover:underline">
              open trial
            </button>
          ) : (
            <span className="text-amber-700">unmatched source</span>
          )}
        </div>
      ))}
    </div>
  );
}

const Aug2026ReadoutTrackerView: React.FC = () => {
  const [trials, setTrials] = useState<ReadoutTrial[]>([]);
  const [updates, setUpdates] = useState<ReadoutUpdate[]>([]);
  const [allCompanies, setAllCompanies] = useState<ReadoutCompany[]>([]);
  const [alerts, setAlerts] = useState<ReadoutAlert[]>([]);
  const [lastRun, setLastRun] = useState<ReadoutRun | null>(null);
  const [lastCrawl, setLastCrawl] = useState<ReadoutLastCrawl | null>(null);
  const [nWithGuidance, setNWithGuidance] = useState(0);
  const [enabledUpdateTypes, setEnabledUpdateTypes] = useState<UpdateTypeId[]>([
    'new_data', 'new_guidance', 'other_update',
  ]);
  const [unmatched, setUnmatched] = useState<UnmatchedCompany[]>([]);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [indication, setIndication] = useState('');
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState<string | null>(null);
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);
  const [firstImminent, setFirstImminent] = useState<TimelineEvent | null>(null);
  const [trialDetail, setTrialDetail] = useState<ReadoutTrial | null>(null);
  const [trialSources, setTrialSources] = useState<TrialSource[]>([]);
  const [companySources, setCompanySources] = useState<{ url: string; hosted_url?: string | null; source_url?: string | null; kind: string; title: string; document_date?: string | null; nct_ids?: string[]; linked?: boolean }[]>([]);
  const [companySourcesLoading, setCompanySourcesLoading] = useState(false);

  const loadMain = useCallback(async (showSpinner = true) => {
    try {
      if (showSpinner) setLoading(true);
      setError(null);
      const data = await apiService.fetchAug2026ReadoutTracker();
      if (data?.error && !data?.trials?.length && !data?.updates?.length && !data?.alerts?.length) {
        setError(String(data.error));
        return;
      }
      setTrials(data?.trials || []);
      setUpdates(data?.updates || []);
      setAllCompanies(data?.companies || []);
      setAlerts(data?.alerts || []);
      setLastRun(data?.last_run || null);
      setLastCrawl(data?.last_crawl || null);
      setNWithGuidance(Number(data?.n_with_guidance || 0));
      setUnmatched(data?.unmatched_companies || []);
      setIsSuperuser(Boolean(data?.is_superuser));
    } catch (err: unknown) {
      const ax = err as { code?: string; message?: string };
      if (ax?.code === 'ECONNABORTED' || /timeout/i.test(ax?.message || '')) {
        setError('Readout tracker timed out. Refresh to try again.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load readout tracker');
      }
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMain();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const indications = useMemo(
    () =>
      Array.from(new Set(trials.map((t) => (t.indication || '').trim()).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b),
      ),
    [trials],
  );

  const cancerTypeOptions = useMemo(
    () =>
      Array.from(new Set(trials.flatMap((t) => trialCancerTypes(t)))).sort((a, b) => a.localeCompare(b)),
    [trials],
  );

  const flaggedIds = useMemo(
    () => new Set(trials.filter((t) => t.flagged).map((t) => t.trial_id)),
    [trials],
  );
  const trialById = useMemo(
    () => new Map(trials.map((t) => [t.trial_id, t])),
    [trials],
  );
  const companyIrLookup = useMemo(() => buildCompanyIrLookup(allCompanies), [allCompanies]);

  const toggleFlag = useCallback(async (trialId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!trialId) return;
    const next = !flaggedIds.has(trialId);
    setTrials((prev) => prev.map((t) => (t.trial_id === trialId ? { ...t, flagged: next } : t)));
    setUpdates((prev) => prev.map((u) => (u.trial_id === trialId ? { ...u, flagged: next } : u)));
    setTrialDetail((prev) => (prev && prev.trial_id === trialId ? { ...prev, flagged: next } : prev));
    try {
      if (next) await apiService.flagAug2026ReadoutTrial(trialId);
      else await apiService.unflagAug2026ReadoutTrial(trialId);
    } catch {
      loadMain(false);
    }
  }, [flaggedIds, loadMain]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return trials.filter((t) => {
      if (indication && (t.indication || '') !== indication) return false;
      if (!q) return true;
      const hay = [
        t.trial_id,
        t.trial_name,
        t.nct_id,
        sponsorsText(t.sponsors),
        t.indication,
        trialCancerTypes(t).join(' '),
        t.timing_phrase,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [trials, query, indication]);

  // Company -> Asset -> Trial drill-down, computed entirely client-side from data already in
  // memory (companies + drug_aliases are already returned per trial by the one existing fetch) —
  // drilling in never triggers a network request, only opening a trial's full timeline does.
  // Lives in the URL (not local state) so the browser's own Back/Forward buttons — and the
  // explicit ones below — actually move between drill levels instead of leaving the page.
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const selectedCompany = searchParams.get('company');
  const selectedAsset = searchParams.get('asset');
  const selectedCancerType = searchParams.get('cancer_type');
  const selectedCancerKey = selectedCancerType ? normalizeKey(selectedCancerType) : null;
  const selectedTrialId = searchParams.get('trial');
  const selectedCompanyRecord = useMemo(() => {
    if (!selectedCompany) return null;
    return allCompanies.find((c) => normalizeKey(companyDisplayName(c)) === selectedCompany)
      || allCompanies.find((c) => normalizeKey(String(c.ticker || '')) === selectedCompany)
      || null;
  }, [allCompanies, selectedCompany]);
  const browseView = searchParams.get('view');
  const sortKey: TrackerSortKey = searchParams.get('sort') === 'event' ? 'event' : 'readout';
  const sortDirParam = searchParams.get('dir');
  const sortDir: TrackerSortDir = sortDirParam === 'asc' || sortDirParam === 'desc'
    ? sortDirParam
    : defaultSortDir(sortKey);
  const showPast = searchParams.get('past') === '1';
  const pastCutoffIso = useMemo(() => oneYearAgoIso(), []);
  const drillLevel: DrillLevel = selectedAsset
    ? 'trials'
    : selectedCompany
      ? 'assets'
      : selectedCancerType
        ? 'cancer_type'
        : browseView === 'companies'
          ? 'companies'
          : browseView === 'flagged'
            ? 'flagged'
            : 'updates';

  const companyItems: HierarchyItem[] = useMemo(() => {
    const map = new Map<string, { name: string; trialIds: Set<string>; assets: Set<string>; lastCrawledAt?: string | null; irUrl?: string | null; unlinkedFileCount?: number }>();
    // Seed every known company first (even ones with zero tracked trials) so readout_companies
    // is the source of truth for "which companies exist," not just whichever ones happen to
    // have a linked watchlist trial today.
    allCompanies.forEach((c) => {
      const name = companyDisplayName(c);
      const key = normalizeKey(name);
      if (!map.has(key)) {
        map.set(key, {
          name,
          trialIds: new Set<string>(),
          assets: new Set<string>(),
          lastCrawledAt: c.last_crawled_at || null,
          irUrl: httpUrl(c.ir_url) || irUrlForCompanyName(name, companyIrLookup),
          unlinkedFileCount: Number(c.unlinked_file_count || 0),
        });
      }
    });
    filtered.forEach((t) => {
      trialCompanies(t).forEach((name) => {
        const key = normalizeKey(name);
        const entry = map.get(key) || {
          name,
          trialIds: new Set<string>(),
          assets: new Set<string>(),
          irUrl: irUrlForCompanyName(name, companyIrLookup),
        };
        if (!entry.irUrl) entry.irUrl = irUrlForCompanyName(name, companyIrLookup);
        entry.trialIds.add(t.trial_id);
        trialAssets(t).forEach((a) => entry.assets.add(normalizeKey(a)));
        map.set(key, entry);
      });
    });
    return Array.from(map.entries())
      .map(([key, v]) => ({
        key,
        name: v.name,
        trialCount: v.trialIds.size,
        assetCount: v.assets.size,
        lastCrawledAt: v.lastCrawledAt,
        irUrl: v.irUrl,
        unlinkedFileCount: v.unlinkedFileCount || 0,
      }))
      .sort((a, b) => (a.name === UNMATCHED_COMPANY ? 1 : b.name === UNMATCHED_COMPANY ? -1 : b.trialCount - a.trialCount));
  }, [filtered, allCompanies, companyIrLookup]);

  const trialsForCompany = useMemo(() => {
    if (!selectedCompany) return [];
    return filtered.filter((t) => trialCompanies(t).some((c) => normalizeKey(c) === selectedCompany));
  }, [filtered, selectedCompany]);

  const assetItems: HierarchyItem[] = useMemo(() => {
    const map = new Map<string, { name: string; trialIds: Set<string> }>();
    trialsForCompany.forEach((t) => {
      trialAssets(t).forEach((name) => {
        const key = normalizeKey(name);
        const entry = map.get(key) || { name, trialIds: new Set<string>() };
        entry.trialIds.add(t.trial_id);
        map.set(key, entry);
      });
    });
    return Array.from(map.entries())
      .map(([key, v]) => ({ key, name: v.name, trialCount: v.trialIds.size }))
      .sort((a, b) => (a.name === UNSPECIFIED_ASSET ? 1 : b.name === UNSPECIFIED_ASSET ? -1 : b.trialCount - a.trialCount));
  }, [trialsForCompany]);

  const trialsForAsset = useMemo(() => {
    if (!selectedAsset) return [];
    const pool = selectedCompany ? trialsForCompany : filtered;
    return pool.filter((t) => trialAssets(t).some((a) => normalizeKey(a) === selectedAsset));
  }, [filtered, trialsForCompany, selectedCompany, selectedAsset]);

  const trialsForCancerType = useMemo(() => {
    if (!selectedCancerType) return [];
    return filtered.filter((t) => trialCancerTypes(t).some((c) => normalizeKey(c) === selectedCancerKey));
  }, [filtered, selectedCancerKey]);

  const updateRecency = useMemo(() => {
    const map = new Map<string, string>();
    updates.forEach((u) => {
      const ts = formatDocDate(u.document_date);
      if (!ts) return;
      const prev = map.get(u.trial_id) || '';
      if (ts > prev) map.set(u.trial_id, ts);
    });
    return map;
  }, [updates]);

  const trialReadoutDate = (t: ReadoutTrial) => readoutSortDate(t);
  const trialEventDate = (t: ReadoutTrial) => eventSortDate(t, updateRecency.get(t.trial_id));

  const trialsForCancerTypeResult = useMemo(() => {
    return applySortHide(
      trialsForCancerType, trialReadoutDate, trialEventDate,
      sortKey, sortDir, showPast, pastCutoffIso,
    );
  }, [trialsForCancerType, updateRecency, sortKey, sortDir, showPast, pastCutoffIso]);
  const trialsForCancerTypeSorted = trialsForCancerTypeResult.visible;

  const flaggedTrialsResult = useMemo(() => {
    return applySortHide(
      filtered.filter((t) => t.flagged), trialReadoutDate, trialEventDate,
      sortKey, sortDir, showPast, pastCutoffIso,
    );
  }, [filtered, updateRecency, sortKey, sortDir, showPast, pastCutoffIso]);
  const flaggedTrialsSorted = flaggedTrialsResult.visible;

  const trialsForAssetResult = useMemo(() => {
    return applySortHide(
      trialsForAsset, trialReadoutDate, trialEventDate,
      sortKey, sortDir, showPast, pastCutoffIso,
    );
  }, [trialsForAsset, updateRecency, sortKey, sortDir, showPast, pastCutoffIso]);
  const trialsForAssetSorted = trialsForAssetResult.visible;

  const cancerTypeAssetItems: HierarchyItem[] = useMemo(() => {
    const map = new Map<string, { name: string; trialIds: Set<string> }>();
    trialsForCancerType.forEach((t) => {
      trialAssets(t).forEach((name) => {
        const key = normalizeKey(name);
        const entry = map.get(key) || { name, trialIds: new Set<string>() };
        entry.trialIds.add(t.trial_id);
        map.set(key, entry);
      });
    });
    return Array.from(map.entries())
      .map(([key, v]) => ({ key, name: v.name, trialCount: v.trialIds.size }))
      .sort((a, b) => {
        const latest = (item: HierarchyItem) =>
          trialsForCancerType
            .filter((t) => trialAssets(t).some((x) => normalizeKey(x) === item.key))
            .reduce((acc, t) => {
              const ts = updateRecency.get(t.trial_id) || '';
              return ts > acc ? ts : acc;
            }, '');
        return latest(b).localeCompare(latest(a)) || b.trialCount - a.trialCount;
      });
  }, [trialsForCancerType, updateRecency]);

  // Each of these pushes a new browser history entry (setSearchParams' default), so the
  // native Back/Forward buttons — and the explicit ones in the breadcrumb bar — both work.
  const mergeParams = (next: Record<string, string | null>) => {
    const merged = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([k, v]) => {
      if (v) merged.set(k, v);
      else merged.delete(k);
    });
    setSearchParams(merged);
  };
  const openCompany = (key: string) => mergeParams({
    company: key, asset: null, cancer_type: null, view: null, trial: selectedTrialId,
  });
  const openAsset = (key: string) => mergeParams({
    company: selectedCompany || '', asset: key, cancer_type: null, view: null, trial: selectedTrialId,
  });
  const openCancerType = (key: string) => mergeParams({
    cancer_type: key, company: null, asset: null, view: null, trial: selectedTrialId,
  });
  const goToUpdates = () => mergeParams({
    company: null, asset: null, cancer_type: null, view: null, trial: selectedTrialId,
  });
  const goToCompanies = () => mergeParams({
    company: null, asset: null, cancer_type: null, view: 'companies', trial: selectedTrialId,
  });
  const goToFlagged = () => mergeParams({
    company: null, asset: null, cancer_type: null, view: 'flagged', trial: selectedTrialId,
  });
  const goToCompany = () => mergeParams({
    company: selectedCompany || '', asset: null, cancer_type: null, view: null, trial: selectedTrialId,
  });
  const setTrackerSort = (key: TrackerSortKey) => {
    if (key === sortKey) {
      const nextDir: TrackerSortDir = sortDir === 'asc' ? 'desc' : 'asc';
      mergeParams({
        sort: key === 'readout' ? null : 'event',
        dir: nextDir === defaultSortDir(key) ? null : nextDir,
      });
      return;
    }
    mergeParams({
      sort: key === 'readout' ? null : 'event',
      dir: null,
    });
  };
  const toggleShowPast = () => mergeParams({ past: showPast ? null : '1' });
  const closeTrial = () => {
    mergeParams({ trial: null });
    setEditingTimelineId(null);
    setAddingTimelineEntry(false);
    setTimelineSaveError(null);
    setTrialDetail(null);
    setTrialSources([]);
  };
  const selectedCompanyItem = companyItems.find((c) => c.key === selectedCompany)
    || companyItems.find((c) => normalizeKey(tickerFromCompanyLabel(c.name)) === selectedCompany)
    || companyItems.find((c) => c.key.includes(selectedCompany || '') && Boolean(selectedCompany));
  const selectedCompanyName = selectedCompanyItem?.name || selectedCompany || '';
  const selectedCompanyIrUrl = httpUrl(selectedCompanyItem?.irUrl)
    || irUrlForCompanyName(selectedCompanyName, companyIrLookup)
    || irUrlForCompanyName(selectedCompany || '', companyIrLookup);
  const selectedAssetName = assetItems.find((a) => a.key === selectedAsset)?.name || selectedAsset || '';
  const selectedCancerTypeName = cancerTypeOptions.find((c) => normalizeKey(c) === selectedCancerKey) || selectedCancerType || '';

  const visibleUpdatesResult = useMemo(() => {
    const q = query.trim().toLowerCase();
    const typeSet = new Set(enabledUpdateTypes);
    const phaseByTrial = new Map<string, string | string[] | null | undefined>();
    trials.forEach((t) => phaseByTrial.set(t.trial_id, t.phase ?? t.phases));
    const filteredUpdates = updates.filter((u) => {
      const ut = (u.update_type || 'other_update') as UpdateTypeId;
      if (typeSet.size > 0 && typeSet.size < UPDATE_TYPE_OPTIONS.length && !typeSet.has(ut)) return false;
      if (indication && (u.indication || '') !== indication) return false;
      if (selectedCancerKey && !trialCancerTypes(u).some((c) => normalizeKey(c) === selectedCancerKey)) return false;
      if (selectedCompany) {
        const companies = toList(u.companies);
        const match = companies.some((c) => normalizeKey(c) === selectedCompany)
          || (selectedCompany === normalizeKey(UNMATCHED_COMPANY) && companies.length === 0);
        if (!match) return false;
      }
      if (selectedAsset) {
        const assets = toList(u.assets);
        const match = assets.some((a) => normalizeKey(a) === selectedAsset)
          || (selectedAsset === normalizeKey(UNSPECIFIED_ASSET) && assets.length === 0);
        if (!match) return false;
      }
      if (drillLevel === 'flagged' && !flaggedIds.has(u.trial_id) && !u.flagged) return false;
      if (!q) return true;
      const hay = [
        u.trial_id, u.trial_name, u.nct_id, sponsorsText(u.companies),
        toList(u.assets).join(' '), trialCancerTypes(u).join(' '),
        formatPhaseLabel(u.phase ?? u.phases ?? phaseByTrial.get(u.trial_id)),
        u.source_name, u.summary, u.headline, u.statement_text, u.indication, u.update_type,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    }).map((u) => (
      u.phase || u.phases
        ? u
        : { ...u, phase: phaseByTrial.get(u.trial_id) ?? null }
    ));
    return applySortHide(
      filteredUpdates,
      (u) => readoutSortDate(trialById.get(u.trial_id) || u),
      (u) => eventSortDate(u),
      sortKey,
      sortDir,
      showPast,
      pastCutoffIso,
    );
  }, [updates, trials, trialById, query, indication, selectedCancerKey, selectedCompany, selectedAsset, enabledUpdateTypes, drillLevel, flaggedIds, sortKey, sortDir, showPast, pastCutoffIso]);

  const updatesHiddenPast = visibleUpdatesResult.hiddenPast;
  const visibleUpdates = visibleUpdatesResult.visible;

  const visibleFiles = useMemo(() => {
    const seen = new Set<string>();
    const files: { url: string; hosted_url?: string | null; source_url?: string | null; kind: string; title: string; trial_id: string; trial_name?: string | null; nct_id?: string | null; assets: string[]; companies: string[] }[] = [];
    visibleUpdates.forEach((u) => {
      const url = fileOpenUrl(u) || (u.source_url || '').trim();
      if (!url || seen.has(`${url}|${u.trial_id}`)) return;
      seen.add(`${url}|${u.trial_id}`);
      files.push({
        url,
        hosted_url: u.hosted_url,
        source_url: u.source_url,
        kind: u.source_kind || 'press_release',
        title: u.source_name || url,
        trial_id: u.trial_id,
        trial_name: u.trial_name,
        nct_id: u.nct_id,
        assets: toList(u.assets),
        companies: toList(u.companies),
      });
    });
    return files;
  }, [visibleUpdates]);

  const loadTrialDetail = useCallback(async (trialId: string) => {
    setTimelineLoading(true);
    setTimelineError(null);
    try {
      const data = await apiService.fetchAug2026ReadoutTrial(trialId);
      if (data?.error) throw new Error(data.error);
      setTrialDetail(data?.trial || null);
      setTimelineEvents(data?.events || []);
      setFirstImminent(data?.first_imminent || null);
      setTrialSources(data?.sources || []);
    } catch (err: unknown) {
      try {
        const data = await apiService.fetchAug2026ReadoutTimeline(trialId);
        if (data?.error) throw new Error(data.error);
        setTrialDetail(trials.find((t) => t.trial_id === trialId) || null);
        setTimelineEvents(data?.events || []);
        setFirstImminent(data?.first_imminent || null);
        setTrialSources([]);
      } catch (inner: unknown) {
        setTimelineError(inner instanceof Error ? inner.message : 'Failed to load trial');
      }
    } finally {
      setTimelineLoading(false);
    }
  }, [trials]);

  const openTimeline = useCallback((trialId: string) => {
    mergeParams({
      company: selectedCompany,
      asset: selectedAsset,
      trial: trialId,
    });
  }, [selectedCompany, selectedAsset, searchParams]);

  useEffect(() => {
    if (!selectedTrialId) {
      setTrialDetail(null);
      setTrialSources([]);
      setTimelineEvents([]);
      setFirstImminent(null);
      return;
    }
    loadTrialDetail(selectedTrialId);
  }, [selectedTrialId, loadTrialDetail]);

  useEffect(() => {
    if (!selectedCompany) {
      setCompanySources([]);
      setCompanySourcesLoading(false);
      return;
    }
    const ticker = String(selectedCompanyRecord?.ticker || tickerFromCompanyLabel(selectedCompanyName) || '').trim();
    const companyId = selectedCompanyRecord?.company_id != null ? String(selectedCompanyRecord.company_id) : '';
    if (!ticker && !companyId) {
      setCompanySources([]);
      return;
    }
    let cancelled = false;
    setCompanySourcesLoading(true);
    apiService.fetchAug2026ReadoutCompanySources({
      ticker: ticker || undefined,
      company_id: companyId || undefined,
    }).then((data) => {
      if (cancelled) return;
      const rows = (data?.unlinked || data?.files || []) as typeof companySources;
      setCompanySources(rows);
    }).catch(() => {
      if (!cancelled) setCompanySources([]);
    }).finally(() => {
      if (!cancelled) setCompanySourcesLoading(false);
    });
    return () => { cancelled = true; };
  }, [selectedCompany, selectedCompanyRecord, selectedCompanyName]);

  // ---- Admin editing state (superuser only) ----
  const [editingWatchlistTrial, setEditingWatchlistTrial] = useState<ReadoutTrial | null>(null);
  const [watchlistForm, setWatchlistForm] = useState<WatchlistFormValue | null>(null);
  const [watchlistSaving, setWatchlistSaving] = useState(false);
  const [watchlistSaveError, setWatchlistSaveError] = useState<string | null>(null);

  const [editingTimelineId, setEditingTimelineId] = useState<number | null>(null);
  const [timelineForm, setTimelineForm] = useState<TimelineEntryFormValue>(BLANK_TIMELINE_FORM);
  const [addingTimelineEntry, setAddingTimelineEntry] = useState(false);
  const [timelineSaving, setTimelineSaving] = useState(false);
  const [timelineSaveError, setTimelineSaveError] = useState<string | null>(null);

  const startEditWatchlist = (t: ReadoutTrial) => {
    setEditingWatchlistTrial(t);
    setWatchlistSaveError(null);
    setWatchlistForm({
      trial_name: t.trial_name || '',
      nct_id: t.nct_id || '',
      indication: t.indication || '',
      milestone_type: t.milestone_type || '',
      sponsors: toList(t.sponsors).join(', '),
      drug_aliases: toList(t.drug_aliases).join(', '),
      active: true,
    });
  };

  const saveWatchlist = async () => {
    if (!editingWatchlistTrial || !watchlistForm) return;
    setWatchlistSaving(true);
    setWatchlistSaveError(null);
    try {
      const res = await apiService.updateAug2026ReadoutWatchlist(editingWatchlistTrial.trial_id, {
        trial_name: watchlistForm.trial_name,
        nct_id: watchlistForm.nct_id,
        indication: watchlistForm.indication,
        milestone_type: watchlistForm.milestone_type,
        sponsors: watchlistForm.sponsors.split(',').map((s) => s.trim()).filter(Boolean),
        drug_aliases: watchlistForm.drug_aliases.split(',').map((s) => s.trim()).filter(Boolean),
        active: watchlistForm.active,
      });
      if (res?.error) throw new Error(res.error);
      setEditingWatchlistTrial(null);
      setWatchlistForm(null);
      await loadMain(false);
    } catch (err: unknown) {
      setWatchlistSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setWatchlistSaving(false);
    }
  };

  const startEditTimelineEntry = (e: TimelineEvent) => {
    setEditingTimelineId(e.id);
    setAddingTimelineEntry(false);
    setTimelineForm(eventToFormValue(e));
    setTimelineSaveError(null);
  };

  const startAddTimelineEntry = () => {
    setAddingTimelineEntry(true);
    setEditingTimelineId(null);
    setTimelineForm(BLANK_TIMELINE_FORM);
    setTimelineSaveError(null);
  };

  const cancelTimelineForm = () => {
    setEditingTimelineId(null);
    setAddingTimelineEntry(false);
    setTimelineSaveError(null);
  };

  const saveTimelineEntry = async () => {
    if (!selectedTrialId) return;
    setTimelineSaving(true);
    setTimelineSaveError(null);
    try {
      const res = editingTimelineId != null
        ? await apiService.updateAug2026ReadoutTimelineEntry(editingTimelineId, { ...timelineForm })
        : await apiService.createAug2026ReadoutTimelineEntry({ trial_id: selectedTrialId, ...timelineForm });
      if (res?.error) throw new Error(res.error);
      setEditingTimelineId(null);
      setAddingTimelineEntry(false);
      await loadTrialDetail(selectedTrialId);
      await loadMain(false);
    } catch (err: unknown) {
      setTimelineSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setTimelineSaving(false);
    }
  };

  const deleteTimelineEntry = async (id: number) => {
    if (!selectedTrialId) return;
    if (!window.confirm('Delete this readout statement? This cannot be undone.')) return;
    setTimelineSaving(true);
    try {
      const res = await apiService.deleteAug2026ReadoutTimelineEntry(id);
      if (res?.error) throw new Error(res.error);
      await loadTrialDetail(selectedTrialId);
      await loadMain(false);
    } catch (err: unknown) {
      setTimelineSaveError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setTimelineSaving(false);
    }
  };

  // ---- Recent alerts, scoped to whatever company/asset the user has drilled into ----
  const visibleTrialIds = useMemo(() => {
    const source = drillLevel === 'trials'
      ? trialsForAsset
      : drillLevel === 'assets'
        ? trialsForCompany
        : drillLevel === 'cancer_type'
          ? trialsForCancerType
          : drillLevel === 'flagged'
            ? filtered.filter((t) => t.flagged)
            : filtered;
    return new Set(source.map((t) => t.trial_id));
  }, [drillLevel, trialsForAsset, trialsForCompany, trialsForCancerType, filtered]);

  const visibleAlerts = useMemo(
    () => alerts.filter((a) => visibleTrialIds.has(a.trial_id)),
    [alerts, visibleTrialIds],
  );

  const alertsScopeLabel = selectedAssetName || selectedCompanyName || selectedCancerTypeName
    || (drillLevel === 'flagged' ? 'My trials' : null);

  const updatesEmptyLabel = !showPast && updatesHiddenPast > 0 && visibleUpdates.length === 0
    ? 'No estimated data readouts in the last year. Turn on Show past (>1 year) to see older rows.'
    : undefined;
  const trialsHiddenPast = drillLevel === 'cancer_type'
    ? trialsForCancerTypeResult.hiddenPast
    : drillLevel === 'flagged'
      ? flaggedTrialsResult.hiddenPast
      : drillLevel === 'trials'
        ? trialsForAssetResult.hiddenPast
        : 0;
  const hiddenPastCount = drillLevel === 'companies'
    ? 0
    : (drillLevel === 'trials' ? 0 : updatesHiddenPast) + trialsHiddenPast;
  const pastEmptyTrialsLabel = 'No trials with an estimated data readout in the last year. Turn on Show past (>1 year) to see older rows.';

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
      <Header />
      <main className="w-full max-w-[1400px] mx-auto px-3 sm:px-4 lg:px-6 py-4 space-y-4">
        <section className="bg-white rounded-2xl border border-gray-100 shadow-card px-4 py-4 sm:px-5">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="text-xl font-bold text-slate-900">Public Companies Trial Updates</h1>
            <span className="text-[10px] font-bold uppercase tracking-wide text-amber-800 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5">
              Beta
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Most recent IR press releases and presentation PDFs, with estimated data readout soonest first. Click a
            company, asset, trial, or cancer type to facet; open PR/PDF links directly. Each source is labeled with the
            matching trial and asset. This page is in beta — features and data coverage may change.
          </p>
        </section>

        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: 'Watchlist trials', value: String(trials.length) },
            { label: 'With timing guidance', value: String(nWithGuidance) },
            { label: 'Recent updates shown', value: String(visibleUpdates.length) },
            {
              label: 'Last updated',
              value: formatPacific(lastCrawl?.last_crawl_at || lastRun?.finished_at) || '—',
            },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-xl border border-slate-200 px-4 py-3">
              <div className="text-xl font-bold text-slate-900 leading-tight">{stat.value}</div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-1">{stat.label}</div>
            </div>
          ))}
        </section>
        <p className="text-[11px] text-slate-500 -mt-1">
          Last updated is the latest per-company IR crawl (<span className="font-medium">America/New_York</span>, EST/EDT).
          The daily job rotates through sites (oldest first, ~9 min budget), so crawl dates differ by company until the queue cycles.
        </p>

        {lastRun?.errors ? (
          <div className="bg-slate-100 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-600">
            Last run notes: {lastRun.notes || 'daily'} · docs {lastRun.docs_fetched || 0} fetched /{' '}
            {lastRun.docs_matched || 0} matched / {lastRun.extractions || 0} extractions
            <div className="mt-1 text-slate-500">{lastRun.errors}</div>
          </div>
        ) : null}

        {unmatched.length > 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-950">
            <div className="font-semibold">
              {unmatched.length} unmatched company name{unmatched.length === 1 ? '' : 's'} — left blank for review
            </div>
            <div className="text-amber-800 mt-1">
              Ingest matches existing companies only. Unknown sponsors stay empty until someone assigns them.
            </div>
            <div className="mt-2 text-xs text-amber-900 space-y-0.5">
              {unmatched.slice(0, 8).map((f) => (
                <div key={f.id}>
                  {f.trial_id || f.source_id}: {f.raw_name}
                </div>
              ))}
              {unmatched.length > 8 ? <div>…and {unmatched.length - 8} more</div> : null}
            </div>
            {isSuperuser ? (
              <Link
                to="/superuser/readout-companies"
                className="inline-block mt-2 text-sm font-semibold text-amber-900 underline"
              >
                Open review portal
              </Link>
            ) : (
              <div className="mt-2 text-xs text-amber-800">Ask an admin to assign these in Superuser → Readout companies.</div>
            )}
          </div>
        ) : null}

        <section className="bg-white rounded-xl border border-slate-200">
          <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by trial, NCT, company, asset, cancer type…"
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm w-full min-w-[12rem] sm:w-72 sm:flex-none"
            />
            <select
              value={selectedCancerTypeName || ''}
              onChange={(e) => {
                const v = e.target.value;
                if (v) openCancerType(v);
                else goToUpdates();
              }}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm max-w-full sm:max-w-[16rem] shrink-0"
            >
              <option value="">All cancer types</option>
              {cancerTypeOptions.map((ct) => (
                <option key={ct} value={ct}>
                  {ct}
                </option>
              ))}
            </select>
            <select
              value={indication}
              onChange={(e) => setIndication(e.target.value)}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm max-w-full sm:max-w-[16rem] shrink-0"
            >
              <option value="">All indications</option>
              {indications.map((ind) => (
                <option key={ind} value={ind}>
                  {ind}
                </option>
              ))}
            </select>
            <div className="flex flex-wrap items-center gap-1.5 shrink-0">
              {UPDATE_TYPE_OPTIONS.map((opt) => {
                const on = enabledUpdateTypes.includes(opt.id);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => {
                      setEnabledUpdateTypes((prev) => {
                        if (prev.includes(opt.id)) {
                          const next = prev.filter((id) => id !== opt.id);
                          return next.length ? next : prev;
                        }
                        return [...prev, opt.id];
                      });
                    }}
                    className={`rounded-full px-2.5 py-1 text-[11px] font-semibold border ${
                      on
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <span className="text-xs text-slate-500">
              {visibleUpdates.length} updates · {filtered.length} of {trials.length} trials
              {formatPacific(lastCrawl?.last_crawl_at || lastRun?.finished_at)
                ? ` · Last updated ${formatPacific(lastCrawl?.last_crawl_at || lastRun?.finished_at)}`
                : ''}
            </span>
            <span className="text-[11px] text-slate-400">
              New Data = new efficacy/safety endpoint results only.
            </span>
            <div className="flex flex-wrap items-center gap-2 basis-full">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Sort</span>
              {([
                { id: 'readout' as const, label: ESTIMATED_READOUT_LABEL },
                { id: 'event' as const, label: `${EVENT_DATE_LABEL} (document date)` },
              ]).map((opt) => {
                const on = sortKey === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setTrackerSort(opt.id)}
                    className={`rounded-full px-2.5 py-1 text-[11px] font-semibold border inline-flex items-center gap-1 ${
                      on
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'
                    }`}
                  >
                    {opt.label}
                    {on ? (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : null}
                  </button>
                );
              })}
              <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer sm:ml-2">
                <input
                  type="checkbox"
                  checked={showPast}
                  onChange={toggleShowPast}
                  className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                Show past (&gt;1 year)
              </label>
              {!showPast && hiddenPastCount > 0 ? (
                <span className="text-[11px] text-slate-400">
                  {hiddenPastCount} hidden as older than 1 year
                </span>
              ) : null}
            </div>
          </div>

          {/* Breadcrumb — Companies > CompanyName > AssetName, plus explicit Back/Forward. Every
              step lives in the URL (setSearchParams), so the browser's own history buttons work
              too — not just these. */}
          <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-1.5 text-sm flex-wrap">
            <button type="button" onClick={() => navigate(-1)} title="Back" aria-label="Back" className="inline-flex items-center justify-center p-1 rounded border border-slate-200 hover:bg-slate-100 text-slate-600">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => navigate(1)} title="Forward" aria-label="Forward" className="inline-flex items-center justify-center p-1 rounded border border-slate-200 hover:bg-slate-100 text-slate-600 mr-1">
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={goToUpdates}
              className={`inline-flex items-center gap-1.5 font-semibold ${drillLevel === 'updates' ? 'text-slate-900' : 'text-indigo-600 hover:underline'}`}
            >
              <FileText className="w-3.5 h-3.5" /> Updates
            </button>
            <button
              type="button"
              onClick={goToCompanies}
              className={`inline-flex items-center gap-1.5 font-semibold ${drillLevel === 'companies' ? 'text-slate-900' : 'text-indigo-600 hover:underline'}`}
            >
              <Building2 className="w-3.5 h-3.5" /> Companies
            </button>
            <button
              type="button"
              onClick={goToFlagged}
              className={`inline-flex items-center gap-1.5 font-semibold rounded-full px-2 py-0.5 ${
                drillLevel === 'flagged'
                  ? 'text-amber-800 bg-amber-50'
                  : 'text-indigo-600 hover:underline'
              }`}
            >
              <Star className={`w-3.5 h-3.5 ${drillLevel === 'flagged' || flaggedIds.size > 0 ? 'text-amber-500' : ''}`} fill={flaggedIds.size > 0 ? 'currentColor' : 'none'} />
              My trials
              {flaggedIds.size > 0 ? (
                <span className="text-[10px] font-bold text-amber-700">{flaggedIds.size}</span>
              ) : null}
            </button>
            {selectedCancerType ? (
              <>
                <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
                <span className="inline-flex items-center gap-1.5 font-semibold text-slate-900">
                  <Activity className="w-3.5 h-3.5" /> {selectedCancerTypeName}
                </span>
              </>
            ) : null}
            {selectedCompany ? (
              <>
                <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
                <button
                  type="button"
                  onClick={goToCompany}
                  className={`inline-flex items-center gap-1.5 font-semibold ${drillLevel === 'assets' ? 'text-slate-900' : 'text-indigo-600 hover:underline'}`}
                >
                  <Pill className="w-3.5 h-3.5" /> {selectedCompanyName}
                </button>
                {selectedCompanyItem?.lastCrawledAt ? (
                  <span className="text-[11px] font-normal text-slate-500">
                    Last crawl {formatPacific(selectedCompanyItem.lastCrawledAt)}
                  </span>
                ) : null}
                {selectedCompanyIrUrl ? (
                  <a
                    href={selectedCompanyIrUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:underline"
                  >
                    Investor relations
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : null}
              </>
            ) : null}
            {selectedAsset ? (
              <>
                <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
                <span className="inline-flex items-center gap-1.5 font-semibold text-slate-900">
                  <FlaskConical className="w-3.5 h-3.5" /> {selectedAssetName}
                </span>
              </>
            ) : null}
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </div>
          ) : error && !trials.length && !updates.length ? (
            <div className="p-6 text-rose-700 text-sm">{error}</div>
          ) : drillLevel === 'updates' ? (
            <UpdatesTable
              rows={visibleUpdates}
              flaggedIds={flaggedIds}
              trialById={trialById}
              irLookup={companyIrLookup}
              onToggleFlag={toggleFlag}
              onOpenTrial={openTimeline}
              onOpenCompany={openCompany}
              onOpenAsset={openAsset}
              onOpenCancer={openCancerType}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={setTrackerSort}
              emptyLabel={updatesEmptyLabel}
            />
          ) : drillLevel === 'companies' ? (
            <HierarchyGrid
              items={companyItems}
              icon={<Building2 className="w-4 h-4 text-indigo-500" />}
              subtitle={(item) => {
                const assets = `${item.assetCount ?? 0} asset${item.assetCount === 1 ? '' : 's'}`;
                const unmatchedN = item.unlinkedFileCount || 0;
                return unmatchedN
                  ? `${assets} · ${unmatchedN} unmatched file${unmatchedN === 1 ? '' : 's'}`
                  : assets;
              }}
              emptyLabel="No companies match."
              onOpen={openCompany}
            />
          ) : drillLevel === 'cancer_type' ? (
            <div>
              <HierarchyGrid
                items={cancerTypeAssetItems}
                icon={<Pill className="w-4 h-4 text-emerald-500" />}
                subtitle={(item) => `${item.trialCount} trial${item.trialCount === 1 ? '' : 's'}`}
                emptyLabel="No upcoming assets for this cancer type."
                onOpen={openAsset}
              />
              <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 border-t border-slate-100">
                Trials
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-[900px] w-full text-xs">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-2 py-2 w-8" aria-label="Flag" />
                      <th className="px-3 py-2 text-left font-semibold">Trial</th>
                      <SortableTh
                        label={ESTIMATED_READOUT_LABEL}
                        column="readout"
                        active={sortKey === 'readout'}
                        dir={sortDir}
                        onSort={setTrackerSort}
                      />
                      <th className="px-3 py-2 text-left font-semibold">NCT</th>
                      <th className="px-3 py-2 text-left font-semibold">Assets</th>
                      <th className="px-3 py-2 text-left font-semibold">Companies</th>
                      <SortableTh
                        label={EVENT_DATE_LABEL}
                        column="event"
                        active={sortKey === 'event'}
                        dir={sortDir}
                        onSort={setTrackerSort}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {trialsForCancerTypeSorted.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                          {!showPast && trialsForCancerTypeResult.hiddenPast > 0
                            ? pastEmptyTrialsLabel
                            : 'No trials for this cancer type.'}
                        </td>
                      </tr>
                    ) : trialsForCancerTypeSorted.map((t) => (
                      <tr key={t.trial_id} onClick={() => openTimeline(t.trial_id)} className="border-t border-slate-100 hover:bg-indigo-50/40 cursor-pointer">
                        <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                          <TrialFlagButton flagged={Boolean(t.flagged)} onToggle={(e) => toggleFlag(t.trial_id, e)} />
                        </td>
                        <td className="px-3 py-2 font-semibold text-indigo-700">{t.trial_name || t.trial_id}</td>
                        <td className="px-3 py-2 min-w-[140px]"><GuidanceBlock trial={t} compact /></td>
                        <td className="px-3 py-2">{t.nct_id || '—'}</td>
                        <td className="px-3 py-2">{trialAssets(t).join(', ')}</td>
                        <td className="px-3 py-2">
                          {trialCompanies(t).map((c) => (
                            <div key={c} className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); openCompany(normalizeKey(c)); }}
                                className="text-left text-indigo-700 hover:underline"
                              >
                                {c}
                              </button>
                              <CompanyIrLink href={irUrlForCompanyName(c, companyIrLookup)} />
                            </div>
                          ))}
                        </td>
                        <td className="px-3 py-2 text-slate-500">{formatDocDate(t.document_date) || formatDocDate(updateRecency.get(t.trial_id)) || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 border-t border-slate-100">
                Updates
              </div>
              <UpdatesTable
                rows={visibleUpdates}
                flaggedIds={flaggedIds}
                trialById={trialById}
                irLookup={companyIrLookup}
                onToggleFlag={toggleFlag}
                onOpenTrial={openTimeline}
                onOpenCompany={openCompany}
                onOpenAsset={openAsset}
                onOpenCancer={openCancerType}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={setTrackerSort}
                emptyLabel={updatesEmptyLabel}
              />
              <FilesStrip files={visibleFiles} onOpenTrial={openTimeline} />
            </div>
          ) : drillLevel === 'assets' ? (
            <div>
              <HierarchyGrid
                items={assetItems}
                icon={<Pill className="w-4 h-4 text-emerald-500" />}
                subtitle={() => null}
                emptyLabel="No assets match."
                onOpen={openAsset}
              />
              <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 border-t border-slate-100">
                Updates &amp; files
              </div>
              <UpdatesTable
                rows={visibleUpdates}
                flaggedIds={flaggedIds}
                trialById={trialById}
                irLookup={companyIrLookup}
                onToggleFlag={toggleFlag}
                onOpenTrial={openTimeline}
                onOpenCompany={openCompany}
                onOpenAsset={openAsset}
                onOpenCancer={openCancerType}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={setTrackerSort}
                emptyLabel={
                  updatesEmptyLabel
                  || (companySources.length > 0
                    ? 'No trial-linked updates yet. Stored IR files are listed below as unmatched sources.'
                    : 'No updates match.')
                }
              />
              <FilesStrip files={visibleFiles} onOpenTrial={openTimeline} />
              {companySourcesLoading ? (
                <div className="px-4 py-3 text-xs text-slate-500 border-t border-slate-100">Loading unmatched IR files…</div>
              ) : (
                <FilesStrip
                  heading="Unmatched sources (not linked to a watchlist trial)"
                  files={companySources.map((f) => ({
                    url: f.url,
                    hosted_url: f.hosted_url,
                    source_url: f.source_url,
                    kind: f.kind || 'press_release',
                    title: f.title,
                    nct_id: (f.nct_ids && f.nct_ids[0]) || null,
                    assets: [],
                    companies: selectedCompanyName ? [selectedCompanyName] : [],
                  }))}
                />
              )}
            </div>
          ) : drillLevel === 'flagged' ? (
            <div>
              <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                My trials
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-[900px] w-full text-xs">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-2 py-2 w-8" aria-label="Flag" />
                      <th className="px-3 py-2 text-left font-semibold">Trial</th>
                      <SortableTh
                        label={ESTIMATED_READOUT_LABEL}
                        column="readout"
                        active={sortKey === 'readout'}
                        dir={sortDir}
                        onSort={setTrackerSort}
                      />
                      <th className="px-3 py-2 text-left font-semibold">NCT</th>
                      <th className="px-3 py-2 text-left font-semibold">Assets</th>
                      <th className="px-3 py-2 text-left font-semibold">Companies</th>
                      <SortableTh
                        label={EVENT_DATE_LABEL}
                        column="event"
                        active={sortKey === 'event'}
                        dir={sortDir}
                        onSort={setTrackerSort}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {flaggedTrialsSorted.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                          {!showPast && flaggedTrialsResult.hiddenPast > 0
                            ? pastEmptyTrialsLabel
                            : 'Star trials to collect them here. Flags are saved to your account.'}
                        </td>
                      </tr>
                    ) : flaggedTrialsSorted.map((t) => (
                      <tr key={t.trial_id} onClick={() => openTimeline(t.trial_id)} className="border-t border-slate-100 hover:bg-indigo-50/40 cursor-pointer">
                        <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                          <TrialFlagButton flagged={Boolean(t.flagged)} onToggle={(e) => toggleFlag(t.trial_id, e)} />
                        </td>
                        <td className="px-3 py-2 font-semibold text-indigo-700">{t.trial_name || t.trial_id}</td>
                        <td className="px-3 py-2 min-w-[140px]"><GuidanceBlock trial={t} compact /></td>
                        <td className="px-3 py-2">{t.nct_id || '—'}</td>
                        <td className="px-3 py-2">{trialAssets(t).join(', ')}</td>
                        <td className="px-3 py-2">
                          {trialCompanies(t).map((c) => (
                            <div key={c} className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); openCompany(normalizeKey(c)); }}
                                className="text-left text-indigo-700 hover:underline"
                              >
                                {c}
                              </button>
                              <CompanyIrLink href={irUrlForCompanyName(c, companyIrLookup)} />
                            </div>
                          ))}
                        </td>
                        <td className="px-3 py-2 text-slate-500">{formatDocDate(t.document_date) || formatDocDate(updateRecency.get(t.trial_id)) || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 border-t border-slate-100">
                Updates
              </div>
              <UpdatesTable
                rows={visibleUpdates}
                flaggedIds={flaggedIds}
                trialById={trialById}
                irLookup={companyIrLookup}
                onToggleFlag={toggleFlag}
                onOpenTrial={openTimeline}
                onOpenCompany={openCompany}
                onOpenAsset={openAsset}
                onOpenCancer={openCancerType}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={setTrackerSort}
                emptyLabel={updatesEmptyLabel}
              />
              <FilesStrip files={visibleFiles} onOpenTrial={openTimeline} />
            </div>
          ) : (
            <div>
            <div className="overflow-x-auto">
              <table className="min-w-[1100px] w-full text-xs">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="px-2 py-2 w-8" aria-label="Flag" />
                    <th className="px-3 py-2 text-left font-semibold">Trial</th>
                    <SortableTh
                      label={ESTIMATED_READOUT_LABEL}
                      column="readout"
                      active={sortKey === 'readout'}
                      dir={sortDir}
                      onSort={setTrackerSort}
                    />
                    <th className="px-3 py-2 text-left font-semibold">NCT</th>
                    <th className="px-3 py-2 text-left font-semibold">Sponsors</th>
                    <th className="px-3 py-2 text-left font-semibold">Indication</th>
                    <th className="px-3 py-2 text-left font-semibold">Cancer type</th>
                    <th className="px-3 py-2 text-left font-semibold">Window</th>
                    <SortableTh
                      label={EVENT_DATE_LABEL}
                      column="event"
                      active={sortKey === 'event'}
                      dir={sortDir}
                      onSort={setTrackerSort}
                    />
                    <th className="px-3 py-2 text-left font-semibold">Source</th>
                    {isSuperuser ? <th className="px-3 py-2 text-left font-semibold">Admin</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {trialsForAssetSorted.length === 0 ? (
                    <tr>
                      <td colSpan={isSuperuser ? 11 : 10} className="px-4 py-10 text-center text-slate-500">
                        {!showPast && trialsForAssetResult.hiddenPast > 0
                          ? pastEmptyTrialsLabel
                          : 'No watchlist trials match.'}
                      </td>
                    </tr>
                  ) : (
                    trialsForAssetSorted.map((t) => (
                      <tr
                        key={t.trial_id}
                        onClick={() => openTimeline(t.trial_id)}
                        className="border-t border-slate-100 align-top hover:bg-indigo-50/40 cursor-pointer"
                      >
                        <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                          <TrialFlagButton flagged={Boolean(t.flagged)} onToggle={(e) => toggleFlag(t.trial_id, e)} />
                        </td>
                        <td className="px-3 py-2 min-w-[180px]">
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); openTimeline(t.trial_id); }}
                              className="font-semibold text-indigo-700 hover:underline text-left"
                            >
                              {t.trial_name && t.trial_name !== t.trial_id && t.trial_name !== t.nct_id
                                ? t.trial_name
                                : t.trial_id}
                            </button>
                            {t.active === false ? (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-500">
                                Inactive
                              </span>
                            ) : null}
                          </div>
                          {t.trial_name && t.trial_name !== t.trial_id ? (
                            <div className="text-slate-400 mt-0.5">{t.trial_id}</div>
                          ) : null}
                          {t.phase || t.phases ? <div className="text-[10px] text-slate-400 mt-0.5">{t.phase || t.phases}</div> : null}
                          {t.milestone_type ? <div className="text-[10px] text-slate-400 mt-0.5">{t.milestone_type}</div> : null}
                        </td>
                        <td className="px-3 py-2 min-w-[160px]">
                          <GuidanceBlock trial={t} compact />
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {t.nct_id ? (
                            <a
                              href={`https://clinicaltrials.gov/study/${t.nct_id}`}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-0.5 text-indigo-600 font-semibold hover:underline"
                            >
                              {t.nct_id}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-3 py-2 min-w-[140px]">
                          {sponsorsText(t.sponsors)}
                          {Array.isArray(t.companies) && t.companies.length > 0 ? (
                            <div className="text-[10px] text-slate-400 mt-0.5">
                              matched: {sponsorsText(t.companies)}
                            </div>
                          ) : Array.isArray(t.companies) ? (
                            <div className="text-[10px] text-amber-700 mt-0.5">no matched company</div>
                          ) : null}
                        </td>
                        <td className="px-3 py-2">{t.indication || '—'}</td>
                        <td className="px-3 py-2">
                          {trialCancerTypes(t).length === 0 ? '—' : trialCancerTypes(t).map((ct) => (
                            <button
                              key={ct}
                              type="button"
                              onClick={(e) => { e.stopPropagation(); openCancerType(ct); }}
                              className="block text-left text-indigo-700 hover:underline"
                            >
                              {ct}
                            </button>
                          ))}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {formatGuidanceLabel(t.timing_phrase)
                            || (t.timing_start
                              ? `${t.timing_start}${t.timing_end && t.timing_end !== t.timing_start ? ` → ${t.timing_end}` : ''}`
                              : '—')}
                        </td>
                        <td className="px-3 py-2">
                          {t.document_date ? (
                            <div className="text-slate-600">{formatDocDate(t.document_date)}</div>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 min-w-[120px]">
                          {fileOpenUrl(t) || t.source_url ? (
                            <SourceLinks
                              url={t.hosted_url || t.source_url}
                              hostedUrl={t.hosted_url}
                              sourceUrl={t.source_url}
                              kind={/\.pdf($|\?)/i.test(t.source_url || '') || /presentation/i.test(t.source_name || '') ? 'presentation_pdf' : 'press_release'}
                              title={t.source_name}
                              onStop={(e) => e.stopPropagation()}
                            />
                          ) : (
                            t.source_name || '—'
                          )}
                        </td>
                        {isSuperuser ? (
                          <td className="px-3 py-2">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); startEditWatchlist(t); }}
                              title="Edit trial fields"
                              className="p-1.5 rounded hover:bg-indigo-100 text-indigo-600"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        ) : null}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <FilesStrip files={visibleFiles} onOpenTrial={openTimeline} />
            </div>
          )}
        </section>

        {alerts.length > 0 ? (
          <section className="bg-white rounded-xl border border-slate-200 px-4 py-4 space-y-3">
            <h2 className="text-sm font-bold text-slate-800">
              Recent alerts{alertsScopeLabel ? <span className="text-slate-400 font-normal"> — {alertsScopeLabel}</span> : null}
            </h2>
            {visibleAlerts.length === 0 ? (
              <div className="text-sm text-slate-500">
                None of the last {alerts.length} alerts touch this selection — showing the most recent {alerts.length} overall isn't scoped further back than that.
              </div>
            ) : (
              visibleAlerts.map((a, idx) => (
                <button
                  key={`${a.trial_id}-${idx}`}
                  type="button"
                  onClick={() => openTimeline(a.trial_id)}
                  className="w-full text-left border-b border-slate-100 last:border-0 pb-3 last:pb-0 hover:bg-indigo-50/40 rounded-lg px-1 -mx-1"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <strong className="text-sm text-indigo-800">
                      {a.trial_name && a.trial_name !== a.trial_id ? a.trial_name : a.trial_id}
                    </strong>
                    {a.nct_id ? <span className="text-xs text-slate-500">{a.nct_id}</span> : null}
                    <span className="text-xs text-slate-400">{shortTs(a.created_at)}</span>
                  </div>
                  {(a.sponsor || a.phase || toList(a.assets).length > 0) ? (
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {[a.sponsor, a.phase, toList(a.assets || a.drug_aliases).slice(0, 2).join(', ')].filter(Boolean).join(' · ')}
                    </div>
                  ) : null}
                  <div className="text-sm text-slate-700 mt-1">{a.message}</div>
                  {fileOpenUrl(a) || a.source_url ? (
                    <span onClick={(e) => e.stopPropagation()}>
                      <SourceLinks
                        url={a.hosted_url || a.source_url}
                        hostedUrl={a.hosted_url}
                        sourceUrl={a.source_url}
                        kind={/\.pdf($|\?)/i.test(a.source_url || '') ? 'presentation_pdf' : 'press_release'}
                        title={a.source_url}
                      />
                    </span>
                  ) : null}
                </button>
              ))
            )}
          </section>
        ) : null}
      </main>

      {selectedTrialId ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          onClick={closeTrial}
        >
          <div
            className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-none">
              <div className="min-w-0 pr-3">
                <h3 className="font-bold text-slate-900 truncate">
                  {trialDetail?.trial_name || selectedTrialId}
                </h3>
                <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                  {trialDetail?.nct_id || selectedTrialId}
                  {trialDetail?.phase ? <span>· {trialDetail.phase}</span> : null}
                  {trialDetail?.overall_status ? <span>· {trialDetail.overall_status}</span> : null}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <TrialFlagButton
                  flagged={Boolean(trialDetail?.flagged) || (selectedTrialId ? flaggedIds.has(selectedTrialId) : false)}
                  onToggle={(e) => toggleFlag(trialDetail?.trial_id || selectedTrialId || '', e)}
                />
                {isSuperuser ? (
                  <button
                    type="button"
                    onClick={startAddTimelineEntry}
                    title="Add a manual readout statement"
                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold text-indigo-600 hover:bg-indigo-50"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add
                  </button>
                ) : null}
                <button type="button" onClick={closeTrial} className="p-1 rounded hover:bg-slate-100">
                  <X className="w-4 h-4 text-slate-500" />
                </button>
              </div>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(80vh-52px)] space-y-3">
              {trialDetail ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 space-y-1">
                  {trialDetail.sponsor || sponsorsText(trialDetail.sponsors) !== '—' ? (
                    <div><span className="font-semibold text-slate-500">Sponsor</span> {trialDetail.sponsor || sponsorsText(trialDetail.sponsors)}</div>
                  ) : null}
                  {trialDetail.indication ? (
                    <div><span className="font-semibold text-slate-500">Indication</span> {trialDetail.indication}</div>
                  ) : null}
                  {trialCancerTypes(trialDetail).length > 0 ? (
                    <div>
                      <span className="font-semibold text-slate-500">Cancer type</span>{' '}
                      {trialCancerTypes(trialDetail).map((ct) => (
                        <button key={ct} type="button" onClick={() => { closeTrial(); openCancerType(ct); }} className="text-indigo-700 hover:underline mr-2">
                          {ct}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <div>
                    <span className="font-semibold text-slate-500">{ESTIMATED_READOUT_LABEL}</span>
                    <div className="mt-1">
                      <GuidanceBlock trial={trialDetail} />
                    </div>
                  </div>
                  {toList(trialDetail.companies).length > 0 ? (
                    <div><span className="font-semibold text-slate-500">Companies</span> {sponsorsText(trialDetail.companies)}</div>
                  ) : null}
                  {toList(trialDetail.drug_aliases).length > 0 ? (
                    <div><span className="font-semibold text-slate-500">Assets</span> {sponsorsText(trialDetail.drug_aliases)}</div>
                  ) : null}
                  {(trialDetail.study_start_date || trialDetail.primary_completion_date || trialDetail.completion_date) ? (
                    <div>
                      <span className="font-semibold text-slate-500">Dates</span>{' '}
                      {[
                        trialDetail.study_start_date ? `start ${String(trialDetail.study_start_date).slice(0, 10)}` : null,
                        trialDetail.primary_completion_date ? `primary ${String(trialDetail.primary_completion_date).slice(0, 10)}` : null,
                        trialDetail.completion_date ? `complete ${String(trialDetail.completion_date).slice(0, 10)}` : null,
                      ].filter(Boolean).join(' · ')}
                    </div>
                  ) : null}
                  {trialDetail.brief_summary ? (
                    <div className="text-slate-600 line-clamp-4">{trialDetail.brief_summary}</div>
                  ) : null}
                  {trialDetail.ctgov_url ? (
                    <a href={trialDetail.ctgov_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-indigo-600 hover:underline">
                      ClinicalTrials.gov <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : null}
                </div>
              ) : null}
              {trialSources.filter((s) => s.kind !== 'ctgov').length > 0 ? (
                <div className="space-y-1.5">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Press releases &amp; PDFs</div>
                  {trialSources.filter((s) => s.kind !== 'ctgov').map((s) => (
                    <div key={`${s.hosted_url || s.url}-${s.source_url || ''}`} className="text-xs">
                      <SourceLinks
                        url={s.url}
                        hostedUrl={s.hosted_url}
                        sourceUrl={s.source_url}
                        kind={s.kind}
                        title={s.title || s.source_name}
                        trialName={trialDetail?.trial_name}
                        nctId={trialDetail?.nct_id}
                        assets={toList(s.assets || trialDetail?.assets || trialDetail?.drug_aliases)}
                      />
                      {s.document_date ? <span className="text-slate-400"> · doc {String(s.document_date).slice(0, 10)}</span> : null}
                      {s.observed_at ? <span className="text-slate-400"> · seen {shortTs(s.observed_at)}</span> : null}
                    </div>
                  ))}
                </div>
              ) : null}
              {timelineSaveError ? <div className="text-sm text-rose-700">{timelineSaveError}</div> : null}
              {addingTimelineEntry ? (
                <TimelineEntryForm
                  value={timelineForm}
                  onChange={setTimelineForm}
                  onSave={saveTimelineEntry}
                  onCancel={cancelTimelineForm}
                  saving={timelineSaving}
                />
              ) : null}
              {timelineLoading ? (
                <div className="text-sm text-slate-500">Loading…</div>
              ) : timelineError ? (
                <div className="text-sm text-rose-700">{timelineError}</div>
              ) : timelineEvents.length === 0 && !addingTimelineEntry ? (
                <div className="text-sm text-slate-500">No timing statements stored yet for this trial.</div>
              ) : (
                <>
                  {firstImminent ? (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                      First called imminent: {firstImminent.observed_at || ''} —{' '}
                      {firstImminent.statement_text || firstImminent.timing_phrase || ''}
                    </div>
                  ) : null}
                  {timelineEvents.map((e) =>
                    editingTimelineId === e.id ? (
                      <TimelineEntryForm
                        key={e.id}
                        value={timelineForm}
                        onChange={setTimelineForm}
                        onSave={saveTimelineEntry}
                        onCancel={cancelTimelineForm}
                        saving={timelineSaving}
                      />
                    ) : (
                      <div key={e.id} className="border-l-2 border-indigo-200 pl-3 space-y-1 group">
                        <div className="flex items-center gap-2 flex-wrap">
                          <strong className="text-sm">{formatDocDate(e.document_date) || '—'}</strong>
                          {e.observed_at_ts || e.observed_at ? (
                            <span className="text-xs text-slate-400">seen {formatPacific(e.observed_at_ts || e.observed_at)}</span>
                          ) : null}
                          <SpecPill value={e.timing_specificity} />
                          {isSuperuser ? (
                            <span className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100">
                              <button type="button" onClick={() => startEditTimelineEntry(e)} title="Edit" className="p-1 rounded hover:bg-indigo-100 text-indigo-600">
                                <Pencil className="w-3 h-3" />
                              </button>
                              <button type="button" onClick={() => deleteTimelineEntry(e.id)} title="Delete" className="p-1 rounded hover:bg-rose-100 text-rose-600">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </span>
                          ) : null}
                        </div>
                        {e.timing_phrase ? <div className="text-sm text-slate-800">{formatGuidanceLabel(e.timing_phrase)}</div> : null}
                        {e.statement_text ? <div className="text-xs text-slate-500">{e.statement_text}</div> : null}
                        {fileOpenUrl(e) || e.source_url ? (
                          <SourceLinks
                            url={e.hosted_url || e.source_url}
                            hostedUrl={e.hosted_url}
                            sourceUrl={e.source_url}
                            kind={/\.pdf($|\?)/i.test(e.source_url || '') || /presentation/i.test(e.source_name || '') ? 'presentation_pdf' : 'press_release'}
                            title={e.source_name}
                          />
                        ) : null}
                      </div>
                    ),
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {editingWatchlistTrial && watchlistForm ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => setEditingWatchlistTrial(null)}>
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-slate-900">Edit {editingWatchlistTrial.trial_id}</h3>
              <button type="button" onClick={() => setEditingWatchlistTrial(null)} className="p-1 rounded hover:bg-slate-100">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="p-4 space-y-2.5">
              {watchlistSaveError ? <div className="text-sm text-rose-700">{watchlistSaveError}</div> : null}
              <label className="block text-xs font-semibold text-slate-500">
                Trial name
                <input value={watchlistForm.trial_name} onChange={(e) => setWatchlistForm({ ...watchlistForm, trial_name: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm font-normal" />
              </label>
              <label className="block text-xs font-semibold text-slate-500">
                NCT ID
                <input value={watchlistForm.nct_id} onChange={(e) => setWatchlistForm({ ...watchlistForm, nct_id: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm font-normal" />
              </label>
              <label className="block text-xs font-semibold text-slate-500">
                Indication
                <input value={watchlistForm.indication} onChange={(e) => setWatchlistForm({ ...watchlistForm, indication: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm font-normal" />
              </label>
              <label className="block text-xs font-semibold text-slate-500">
                Milestone type
                <input value={watchlistForm.milestone_type} onChange={(e) => setWatchlistForm({ ...watchlistForm, milestone_type: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm font-normal" />
              </label>
              <label className="block text-xs font-semibold text-slate-500">
                Sponsors (comma-separated)
                <input value={watchlistForm.sponsors} onChange={(e) => setWatchlistForm({ ...watchlistForm, sponsors: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm font-normal" />
              </label>
              <label className="block text-xs font-semibold text-slate-500">
                Drug / asset aliases (comma-separated)
                <input value={watchlistForm.drug_aliases} onChange={(e) => setWatchlistForm({ ...watchlistForm, drug_aliases: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm font-normal" />
              </label>
              <label className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                <input type="checkbox" checked={watchlistForm.active} onChange={(e) => setWatchlistForm({ ...watchlistForm, active: e.target.checked })} />
                Active (shown on the watchlist)
              </label>
              <div className="flex gap-2 pt-1">
                <button type="button" disabled={watchlistSaving} onClick={saveWatchlist}
                  className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold disabled:opacity-50">
                  {watchlistSaving ? 'Saving…' : 'Save'}
                </button>
                <button type="button" disabled={watchlistSaving} onClick={() => setEditingWatchlistTrial(null)}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Aug2026ReadoutTrackerView;
